/* eslint-disable no-restricted-syntax */
import { defineStore } from 'pinia';

import { useAppStore } from './app';

import CustomizerOrderData from './customizer-orderdata';
import CustomizerCart from './customizer-cart';
import CustomizerRendering from './customizer-rendering';

// eslint-disable-next-line no-unused-vars
import emitter from '../helpers/bus';

import { formatPrice } from '../helpers/format';
import { getPlaceholderForPlacement } from '../helpers/products';

import {
    PREVIEW_IMAGE_PAINTFILL_COLOR_PLACEMENTS,
} from '../constants';

const PALETTE_SWATCH_SLOT_COUNT = 4;
const ALL_COLOR_LOCATION_GROUPS = [
    { key: 'top', label: 'Top' },
    { key: 'backLogo', label: 'Logo' },
    { key: 'backCavityDots', label: 'Cavity Dots' },
    { key: 'frontPaintfillWeights', label: 'Weights' },
    { key: 'frontPaintfillLogos', label: 'Logos' },
    { key: 'frontInitials', label: 'Initials' },
    { key: 'frontStamps', label: 'Stamps' },
];

const LOCKED_GROUPS = new Set([
    'Grip',
    'Headcover',
    'Shaft Band',
]);
const FRONT_NESTED_PANEL_SECTIONS = ['initials', 'stamps', 'paintfill'];

const GROUP_LOCK_NAME_ALIASES = {
    ShaftBand: 'Shaft Band',
    PutterShaftBand: 'Shaft Band',
};

const GROUP_LOCK_UNLOCK_PLACEMENTS = [
    'Paintfill',
    'StampingStyle',
    'Stamp',
    'Initials',
    'PutterTopPaintfill',
    'PutterBackPaintfill',
];

const PAINTFILL_GROUPS = {
    'Face/Sole': {
        mainPlacement: 'PutterFrontPaintfill',
    },
    Top: {
        mainPlacement: 'PutterTopPaintfill',
    },
    Back: {
        mainPlacement: 'PutterBackPaintfill',
    },
};
const PAINTFILL_GROUP_ORDER = Object.keys(PAINTFILL_GROUPS);

function buildDefaultPaletteSwatchSlots() {
    return Array.from({ length: PALETTE_SWATCH_SLOT_COUNT }, (_, index) => `__empty__-${index}`);
}

function buildInitialGroupLocks() {
    const groupLocks = {};

    LOCKED_GROUPS.forEach((groupName) => {
        groupLocks[groupName] = true;
    });

    return groupLocks;
}

function normalizeGroupNameForLock(groupOrName) {
    if (!groupOrName) {
        return '';
    }

    const rawGroupName = typeof groupOrName === 'string'
        ? groupOrName
        : (groupOrName.name || groupOrName.code || '');

    return GROUP_LOCK_NAME_ALIASES[rawGroupName] || rawGroupName;
}

function normalizePaintfillGroupName(groupOrName) {
    if (!groupOrName) {
        return '';
    }

    const rawGroupName = typeof groupOrName === 'string'
        ? groupOrName
        : (groupOrName.name || groupOrName.code || '');

    if (rawGroupName === 'Front') {
        return 'Face/Sole';
    }

    return rawGroupName;
}

function emptyPaletteSlotValue(index) {
    return `__empty__-${index}`;
}

function isEmptyPaletteSlotValue(value) {
    return typeof value === 'string' && value.startsWith('__empty__-');
}

function stepSubOptionCollections(stepSubOptions) {
    if (!stepSubOptions) {
        return [];
    }

    return [
        stepSubOptions.top,
        stepSubOptions.backLogo,
        stepSubOptions.backCavityDots,
        stepSubOptions.frontPaintfillWeights,
        stepSubOptions.frontPaintfillLogos,
        stepSubOptions.frontInitials,
        stepSubOptions.frontStamps,
    ].filter(Array.isArray);
}

function findStepSubOption(stepSubOptions, optionName) {
    if (!optionName) {
        return null;
    }

    const collections = stepSubOptionCollections(stepSubOptions);

    // eslint-disable-next-line no-restricted-syntax
    for (const options of collections) {
        const match = options.find((option) => option.name === optionName);

        if (match) {
            return match;
        }
    }

    return null;
}

function selectedColorFromActivePaletteSlot(masterPalette, activeSlotValue) {
    if (!activeSlotValue || isEmptyPaletteSlotValue(activeSlotValue)) {
        return null;
    }

    const valueMatch = masterPalette
        ?.find((paletteOption) => paletteOption.value === activeSlotValue);
    const colorMatch = masterPalette
        ?.find((paletteOption) => paletteOption.color === activeSlotValue);

    return valueMatch?.color || colorMatch?.color || activeSlotValue || null;
}

function buildStepSubOptionPayloadList(options, activePaletteSlotByOptionName, masterPalette) {
    if (!Array.isArray(options)) {
        return [];
    }

    return options.map((option) => {
        const name = option?.name || '';
        const activeSlotValue = activePaletteSlotByOptionName?.[name] || null;
        const derivedSelectedColor = selectedColorFromActivePaletteSlot(masterPalette, activeSlotValue);

        return {
            ...option,
            selectedColor: derivedSelectedColor ?? option?.selectedColor ?? null,
            activeSlotValue,
        };
    });
}

function normalizePaletteSelectionValue(masterPalette, paletteSelection) {
    const nextRawValue = typeof paletteSelection === 'object'
        ? (paletteSelection?.value || paletteSelection?.color || null)
        : paletteSelection;

    if (!nextRawValue) {
        return null;
    }

    const directMatch = masterPalette?.find((option) => option.value === nextRawValue);
    const colorMatch = masterPalette?.find((option) => option.color === nextRawValue);

    return directMatch?.value || colorMatch?.value || nextRawValue;
}

function paletteOptionFromValue(masterPalette, paletteValue) {
    if (!paletteValue || !Array.isArray(masterPalette)) {
        return null;
    }

    return masterPalette.find((paletteOption) => (
        paletteOption.value === paletteValue
        || paletteOption.color === paletteValue
    )) || null;
}

function colorLabelFromPaletteValue(masterPalette, paletteValue) {
    if (!paletteValue) {
        return '';
    }

    const paletteOption = paletteOptionFromValue(masterPalette, paletteValue);
    const rawLabel = `${paletteOption?.label || paletteValue}`.trim();

    return rawLabel
        .replace(/\bpaint\s*fill\b/ig, '')
        .replace(/\bpaint\b/ig, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

function buildColorChangeToastMessage(masterPalette, paletteValue, locationCount, isReplacement) {
    let normalizedCount = Number.isFinite(locationCount) ? locationCount : 0;

    if (!isReplacement) {
        normalizedCount = 1;
    }

    const locationLabel = normalizedCount === 1 ? 'Location' : 'Locations';
    const colorLabel = colorLabelFromPaletteValue(masterPalette, paletteValue) || paletteValue || '';

    return `${normalizedCount} ${locationLabel} Updated To: ${colorLabel}`;
}

function buildAllColorLocationGroups(stepSubOptions, masterPalette) {
    if (!stepSubOptions) {
        return [];
    }

    const activePaletteSlotByOptionName = {
        ...(stepSubOptions.activePaletteSlotByOptionName || {}),
    };

    return ALL_COLOR_LOCATION_GROUPS.map((groupConfig) => {
        const options = buildStepSubOptionPayloadList(
            stepSubOptions[groupConfig.key],
            activePaletteSlotByOptionName,
            masterPalette,
        )
            .filter((option) => !!option.selectedColor)
            .map((option) => ({
                ...option,
                groupKey: groupConfig.key,
                groupLabel: groupConfig.label,
            }));

        return {
            key: groupConfig.key,
            label: groupConfig.label,
            options,
        };
    }).filter((group) => group.options.length > 0);
}

function normalizeStepSubOptionActiveSlotInput(activeSlotInput) {
    if (!activeSlotInput) {
        return null;
    }

    if (typeof activeSlotInput === 'string') {
        return activeSlotInput;
    }

    if (Object.prototype.hasOwnProperty.call(activeSlotInput, 'activeSlotValue')) {
        return activeSlotInput.activeSlotValue || null;
    }

    const slotColor = activeSlotInput.slotColor && activeSlotInput.slotColor !== 'NONE'
        ? activeSlotInput.slotColor
        : null;

    return slotColor
        || activeSlotInput.slotValue
        || activeSlotInput.value
        || activeSlotInput.color
        || null;
}

function stepSubOptionGroups(stepSubOptions) {
    if (!stepSubOptions) {
        return [];
    }

    return [
        {
            key: 'top',
            label: 'Top',
            options: stepSubOptions.top,
        },
        {
            key: 'backLogo',
            label: 'Back Logo',
            options: stepSubOptions.backLogo,
        },
        {
            key: 'backCavityDots',
            label: 'Back Cavity Dots',
            options: stepSubOptions.backCavityDots,
        },
        {
            key: 'frontPaintfillWeights',
            label: 'Front Paintfill Weights',
            options: stepSubOptions.frontPaintfillWeights,
        },
        {
            key: 'frontPaintfillLogos',
            label: 'Front Paintfill Logos',
            options: stepSubOptions.frontPaintfillLogos,
        },
        {
            key: 'frontInitials',
            label: 'Front Initials',
            options: stepSubOptions.frontInitials,
        },
        {
            key: 'frontStamps',
            label: 'Front Stamps',
            options: stepSubOptions.frontStamps,
        },
    ].filter((group) => Array.isArray(group.options));
}

function paintfillGroupNameForPlacementCode(placementCode) {
    if (!placementCode) {
        return null;
    }

    return PAINTFILL_GROUP_ORDER.find((groupName) => (
        PREVIEW_IMAGE_PAINTFILL_COLOR_PLACEMENTS[groupName] || []
    ).includes(placementCode)) || null;
}

function buildStepSubOptionAssignments(stepSubOptions, activeSlotValue) {
    if (!activeSlotValue || !stepSubOptions) {
        return [];
    }

    const mapByOptionName = stepSubOptions.activePaletteSlotByOptionName || {};

    const categoryForAssignment = (groupKey, optionName) => {
        if (groupKey === 'top') {
            return 'Top';
        }

        if (groupKey === 'backLogo' || groupKey === 'backCavityDots') {
            return 'Back';
        }

        if (groupKey === 'frontInitials') {
            return 'Face';
        }

        if (groupKey === 'frontStamps') {
            return 'Face';
        }

        const normalizedName = (optionName || '').toLowerCase();

        if (normalizedName.includes('sole') || normalizedName.includes('outsole') || normalizedName.includes('weight')) {
            return 'Sole';
        }

        if (normalizedName.includes('face')) {
            return 'Face';
        }

        return 'Face/Sole';
    };

    return stepSubOptionGroups(stepSubOptions)
        .flatMap((group) => group.options
            .filter((option) => mapByOptionName[option.name] === activeSlotValue)
            .map((option) => ({
                category: categoryForAssignment(group.key, option.name),
                group: group.label,
                optionName: option.name,
                selectedColor: option.selectedColor || null,
                activeSlotValue,
            })));
}

function areStampsAllowed(controller) {
    if (controller == null) {
        return true;
    }

    const prevents = controller.state
        .some((s) => s.component.custom?.['v-stamping-prevent-toe-engraving'] === 'yes');

    return !prevents;
}

function isGroupComplete(controller, group) {
    if (controller == null || group == null || group.placements == null) {
        return false;
    }

    const totalPlacements = group.placements.length;

    let totalPlacementSelections = 0;
    let requiredTextMissing = false;
    let requiredSymbolMissing = false;

    group.placements.forEach((placement) => {
        const selectedAtPlacement = controller.selectedAtPlacement(placement.code);

        if (selectedAtPlacement && (!selectedAtPlacement.placeholder || selectedAtPlacement.placeholderOptional)) {
            const hasText1 = selectedAtPlacement?.custom?.['v-stamping-has-text-1'] === 'yes';
            const text1Value = selectedAtPlacement?.custom?.['v-stamping-text-1'];
            const hasText2 = selectedAtPlacement?.custom?.['v-stamping-has-text-2'] === 'yes';
            const text2Value = selectedAtPlacement?.custom?.['v-stamping-text-2'];

            if ((hasText1 && !text1Value) || (hasText2 && !text2Value)) {
                requiredTextMissing = true;
            }

            const hasSymbol = selectedAtPlacement?.custom?.['v-stamping-use-symbols-addon'] === 'yes'
                || selectedAtPlacement?.custom?.['v-stamping-has-symbols'] === 'yes';
            const hasPlaceholder = controller.selectedAtPlacement('StampSymbols')?.placeholder;

            if (hasSymbol && hasPlaceholder) {
                requiredSymbolMissing = true;
            }

            if (selectedAtPlacement.code === 'LaserEtching-LaserEtching-Etch') {
                const hasEtchValue = selectedAtPlacement?.custom?.['v-etching-text-1'].length > 0;
                if (!hasEtchValue) {
                    requiredTextMissing = true;
                }
            }

            totalPlacementSelections += 1;
        }
    });

    return totalPlacementSelections === totalPlacements && !requiredTextMissing && !requiredSymbolMissing;
}

function calculateVisibleGroups(controller) {
    // Safety measures, use empty list if there is no controller.
    if (controller == null) {
        return [];
    }

    const groups = controller.blueprint.groups.filter((group) => {
        // Skip groups that are hidden in the UI.
        if (group.custom?.['v-hidden'] === 'true') {
            return false;
        }

        if (group.custom['v-group-type'] === 'stamps') {
            if (!areStampsAllowed(controller)) {
                return false;
            }
        }

        return true;
    });

    return groups;
}

// eslint-disable-next-line import/prefer-default-export
export const useCustomizerStore = defineStore('customizer', {
    id: 'customizer',

    state: () => ({
        // Widget configuration options.
        options: {},

        // Currently selected controller.
        viewController: null,

        // Current vendor data.
        vendor: null,

        // Branding programs for the current vendor.
        brandingPrograms: null,

        // Other translations according to the vendor locale.
        otherTranslations: null,

        // Yet more other translations according to the vendor locale.
        fallbackTranslations: null,

        // This message will be displayed in the PromoMessage component if present.
        specialMessage: null,

        // Complete product data provided by WebTop.
        product: null,

        // Initial instance saved by WebTop.
        initialInstance: null,

        // The very first controller that would allow to reset all selections to the very beginning.
        startingController: null,

        // A stack of captured controllers representing user selections.
        undoStack: [],

        // The currently selected group.
        currentGroup: null,

        // Current calculated price.
        currentPrice: null,

        // Current calculated price.
        currentPriceFormatted: null,

        // Last calculated image URL.
        lastImageUrl: null,

        // Last loaded liquidfire image.
        lastRenderedImage: null,

        // Active view.
        activeView: 'Face',

        // Array of groups seen by the user.
        groupsSeen: [],

        // Manual lock state for lockable groups.
        groupLocks: buildInitialGroupLocks(),

        // Used to display error messages.
        inErrorState: false,

        panelsInErrorState: [],

        // Used to make a matching set of clubs.
        cart: [],

        // Cart item being edited.
        currentCartItem: null,

        clubSetController: null,

        // Initial AGS data.
        initialData: null,

        // UI: persist independent active flags for the integrated Front panel subsections.
        uiFrontActiveSections: {},

        // UI: persist validation attempts for Front nested flyout subsections.
        uiFrontNestedPanelValidationAttempted: {},

        // UI: persist selection state for Front -> No Customization per group
        uiFrontNoCustomizationSelected: {},

        // UI: persist explicit No Customization selections for Top/Back.
        uiTopNoCustomizationSelected: false,
        uiBackNoCustomizationSelected: false,

        initialSet: null,

        // All products in the product line.
        allProducts: [],

        // A list of currently selected products.
        selectedProducts: [],

        // Temporary flag to load the UX in the "empty" state.
        loadAsEmpty: false,

        panelToReset: null,

        hasChanged: false,

        isFinishedLoading: false,

        // Calculated lead time.
        leadTime: 0,

        // Master palette data.
        masterPalette: [],

        // Shared color palette slots used by color controls and "My Palette" display.
        paletteSwatchSlots: buildDefaultPaletteSwatchSlots(),

        // Selection state used by Top/Back/Front Paintfill lists.
        uiStepSubOptions: null,

        // UI: force raw paintfill rendering after clearing all paintfill selections.
        uiPaintfillRawPreviewActive: false,

        // Paintfill sections ordered from least recently to most recently added.
        uiPaintfillSelectionOrder: [],

        // Pending confirmation context when changing an assigned My Palette slot color.
        uiPendingStepSubOptionColorChange: null,
    }),

    getters: {
        /**
         * Check if any component disables stamps.
         */
        allowStamps() {
            return areStampsAllowed(this.viewController);
        },

        /**
         * A list of groups visible in the UI.
         */
        visibleGroups() {
            return calculateVisibleGroups(this.viewController);
        },

        /**
         * Normalized top-level steps used for progress/completion counts.
         */
        progressGroups() {
            return this.buildVisibleGroups({
                sourceGroups: this.visibleGroups,
            });
        },

        progressGroupHasCheckFilled() {
            return (group) => {
                const groupName = typeof group === 'string' ? group : group?.name;

                return !!groupName
                    && this.groupsSeen.includes(groupName)
                    && this.selectionsMade.includes(groupName)
                    && !this.progressGroupHasErrors(group);
            };
        },

        progressGroupHasErrors() {
            return (group) => {
                const groupName = typeof group === 'string' ? group : group?.name;
                const groupCode = typeof group === 'string' ? group : group?.code;
                let paintfillGroupName = null;

                if (!groupName || !this.groupsSeen.includes(groupName)) {
                    return false;
                }

                if (groupName === 'Face/Sole' || groupCode === 'Face/Sole') {
                    paintfillGroupName = 'Face/Sole';
                } else if (groupName === 'Top' || groupCode === 'TopPaintfill') {
                    paintfillGroupName = 'Top';
                } else if (groupName === 'Back' || groupCode === 'BackPaintfill') {
                    paintfillGroupName = 'Back';
                }

                const hasPaintfillErrors = !!paintfillGroupName
                    && this.isAnyPaintfillSelected()
                    && !this.isPaintfillComplete(paintfillGroupName);

                return hasPaintfillErrors
                    || (this.panelsInErrorState.includes('Main') && !this.selectionsMade.includes(groupName));
            };
        },

        /**
         * The name of the product being customized.
         */
        productName() {
            let name = this.viewController?.blueprint.name;

            if (this.viewController?.blueprint.custom['v-ags-orientation-code'] === 'CA-RH') {
                if (!name.includes('- RH')) {
                    name += ' - RH';
                }
            }

            return name;
        },

        /**
         * Extract product price from WebTop data.
         */
        productPrice() {
            if (this.currentPriceFormatted == null) {
                return null;
            }

            return this.currentPriceFormatted.current;
        },

        paintfillSelectionOrder() {
            return (this.uiPaintfillSelectionOrder || [])
                .filter((groupName) => this.hasSelectedPaintfillColorForGroup(groupName));
        },

        leastRecentlyAddedPaintfillSection() {
            return this.paintfillSelectionOrder[0] || null;
        },

        /**
         * Selections made so far.
         */
        selectionsMade() {
            const selections = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const group of this.visibleGroups) {
                if (!this.groupsSeen.includes(group.name)) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                // Special case for Top.
                if (group.name === 'Top' || group.code === 'TopPaintfill') {
                    if (this.isTopPaintfillComplete() || this.isTopNoCustomizationSelected()) {
                        selections.push(group.name);
                    }

                    // eslint-disable-next-line no-continue
                    continue;
                }

                // Special case for Back.
                if (group.name === 'Back' || group.code === 'BackPaintfill') {
                    if (this.isBackPaintfillComplete() || this.isBackNoCustomizationSelected()) {
                        selections.push(group.name);
                    }

                    // eslint-disable-next-line no-continue
                    continue;
                }

                // Special case for clubs.
                if (group.code === 'Specifications') {
                    if (this.selectedProducts.length === 0) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }
                }

                if (isGroupComplete(this.viewController, group)) {
                    selections.push(group.name);
                }
            }

            // Integrated Face/Sole panel: include it if any subgroups are complete or if "No Customization" was selected
            const characterStampingGroup = this.visibleGroups.find((g) => g.name === 'Character Stamping');
            const stampsGroup = this.visibleGroups.find((g) => g.name === 'Stamps');

            const frontComplete = isGroupComplete(this.viewController, characterStampingGroup)
                || isGroupComplete(this.viewController, stampsGroup)
                || this.isPaintfillComplete('Face/Sole')
                || this.isFrontNoCustomizationSelected('Face/Sole');

            if (frontComplete && !selections.includes('Face/Sole')) {
                selections.push('Face/Sole');
            }

            if (this.isTopNoCustomizationSelected() && !selections.includes('Top')) {
                selections.push('Top');
            }

            if (this.isBackNoCustomizationSelected() && !selections.includes('Back')) {
                selections.push('Back');
            }

            return selections;
        },

        /**
         * Total selections made.
         */
        totalSelectionsMade() {
            return this.progressGroups
                .filter((group) => this.progressGroupHasCheckFilled(group))
                .length;
        },

        totalSteps() {
            return this.progressGroups.length;
        },

        /**
         * Determines whether all required selections have been made.
         * Determines whether the Add to Cart button is enabled.
         */
        selectionsComplete() {
            return this.totalSelectionsMade === this.totalSteps && this.totalSteps > 0;
        },

        /**
         * Determines whether a selection has been made by the user.
         */
        userMadeSelection() {
            return this.undoStack.length > 0;
        },

        /**
         * Computed getter to translate things using translations provided by vendor data.
         */
        useTranslations() {
            return (key, defaultText) => {
                // No translations loaded yet.
                if (this.fallbackTranslations == null) {
                    return defaultText;
                }

                if (this.fallbackTranslations[key]) {
                    return this.fallbackTranslations[key];
                }

                return defaultText;
            };
        },

        /**
         * Checks if handground options are selected.
         * @return {[type]} [description]
         */
        handgroundSelected() {
            if (this.viewController == null) {
                return false;
            }

            const handgroundGroup = this.viewController.blueprint.groups.find((g) => g.code === 'Handground');

            if (handgroundGroup == null) {
                return false;
            }

            // eslint-disable-next-line no-restricted-syntax
            for (const placement of handgroundGroup.placements) {
                const code = placement.code;

                const option = this.viewController.selectedAtPlacement(code);

                if (option != null && option.placeholder !== true) {
                    return true;
                }
            }

            return false;
        },

        /**
         * Total set price.
         */
        cartPrice() {
            let value = 0;

            // eslint-disable-next-line no-restricted-syntax
            for (const item of this.cart) {
                const currentPrice = item.controller.statePrice({
                    ignoreOptional: true,
                });

                value += currentPrice.current;
            }

            const locale = this.options.locale ?? 'en-US';
            const currency = this.options.currency ?? 'USD';

            return formatPrice(value, false, locale, currency);
        },

        restrictions() {
            const json = this.viewController?.blueprint?.custom?.['t-restrictions'];

            if (json) {
                try {
                    return JSON.parse(json);
                } catch (e) {
                    return null;
                }
            }

            return null;
        },

        areSpecificationsLocked() {
            return this.options.lockSpecifications ?? false;
        },
    },

    actions: {
        shouldShowColorControls(groupType) {
            if (!['front', 'top', 'back'].includes(groupType)) {
                return false;
            }
            if (groupType === 'front') {
                return this.isFrontSectionActive('paintfill');
            }
            if (groupType === 'top') {
                return this.isTopSectionActive();
            }
            if (groupType === 'back') {
                return this.isBackSectionActive('back');
            }

            return false;
        },

        resetStepSubOptionsState() {
            this.uiStepSubOptions = this.buildStepSubOptions();
            this.uiPaintfillRawPreviewActive = false;
            this.uiPaintfillSelectionOrder = [];
            this.uiPendingStepSubOptionColorChange = null;
        },

        setPaintfillRawPreviewActive(active) {
            this.uiPaintfillRawPreviewActive = !!active;
        },

        hasSelectedPaintfillColorForGroup(groupName) {
            const normalizedGroupName = normalizePaintfillGroupName(groupName);
            const placementCodes = new Set(this.paintfillColorPlacementCodesForGroup(normalizedGroupName));

            if (placementCodes.size === 0) {
                return false;
            }

            return this.paintfillSubOptionKeysForGroup(normalizedGroupName)
                .some((key) => (this.uiStepSubOptions?.[key] || [])
                    .some((option) => placementCodes.has(option.name) && !!option.selectedColor));
        },

        markPaintfillSectionAdded(groupName) {
            const normalizedGroupName = normalizePaintfillGroupName(groupName);

            if (!PAINTFILL_GROUPS[normalizedGroupName]) {
                return;
            }

            this.uiPaintfillSelectionOrder = [
                ...(this.uiPaintfillSelectionOrder || [])
                    .filter((entry) => entry !== normalizedGroupName),
                normalizedGroupName,
            ];
        },

        prunePaintfillSelectionOrder() {
            this.uiPaintfillSelectionOrder = (this.uiPaintfillSelectionOrder || [])
                .filter((groupName) => this.hasSelectedPaintfillColorForGroup(groupName));
        },

        rebuildPaintfillSelectionOrder() {
            this.uiPaintfillSelectionOrder = PAINTFILL_GROUP_ORDER
                .filter((groupName) => this.hasSelectedPaintfillColorForGroup(groupName));
        },

        clearStepSubOptionColorSelections() {
            stepSubOptionCollections(this.uiStepSubOptions).forEach((options) => {
                options.forEach((option) => {
                    option.selectedColor = null;
                });
            });

            this.uiStepSubOptions = {
                ...this.uiStepSubOptions,
                activePaletteSlotByOptionName: {},
            };
            this.uiPendingStepSubOptionColorChange = null;
            this.uiPaintfillSelectionOrder = [];
        },

        clearPaletteSwatchSlots() {
            this.paletteSwatchSlots = buildDefaultPaletteSwatchSlots();

            this.syncMasterPaletteSelectedValues(new Set());

            this.clearStepSubOptionColorSelections();
        },

        getAppliedPaletteValueForStepSubOption(option) {
            if (!option?.name || !this.isColorPlacementSelected(option.name)) {
                return null;
            }

            // Trust controller selections over uiStepSubOptions because palette assignment
            // metadata can outlive removed paintfill placements.
            const selected = this.viewController?.selectedAtPlacement(option.name);
            const selectedColor = selected?.custom?.['v-hex'] || option.selectedColor;

            return normalizePaletteSelectionValue(this.masterPalette, selectedColor);
        },

        syncMasterPaletteSelectedValues(selectedValues) {
            if (!Array.isArray(this.masterPalette)) {
                return;
            }

            this.masterPalette.forEach((paletteOption) => {
                paletteOption.selected = selectedValues.has(paletteOption.value);
            });
        },

        keepPaletteColorsForAppliedGroups(groupKeys = []) {
            const nextActiveByOptionName = {
                ...(this.uiStepSubOptions?.activePaletteSlotByOptionName || {}),
            };
            const nextStepSubOptions = {
                ...this.uiStepSubOptions,
            };
            const preservedValues = new Set();

            groupKeys.forEach((groupKey) => {
                const options = Array.isArray(this.uiStepSubOptions?.[groupKey])
                    ? this.uiStepSubOptions[groupKey]
                    : [];

                nextStepSubOptions[groupKey] = options.map((option) => {
                    const selectedPaletteValue = this.getAppliedPaletteValueForStepSubOption(option);

                    if (!selectedPaletteValue) {
                        delete nextActiveByOptionName[option.name];
                        return {
                            ...option,
                            selectedColor: null,
                        };
                    }

                    nextActiveByOptionName[option.name] = selectedPaletteValue;
                    preservedValues.add(selectedPaletteValue);

                    return {
                        ...option,
                        selectedColor: selectedColorFromActivePaletteSlot(this.masterPalette, selectedPaletteValue),
                    };
                });
            });

            this.uiStepSubOptions = {
                ...nextStepSubOptions,
                activePaletteSlotByOptionName: nextActiveByOptionName,
            };

            this.paletteSwatchSlots = this.paletteSwatchSlots.map((slotValue, index) => {
                const paletteValue = normalizePaletteSelectionValue(this.masterPalette, slotValue);

                if (paletteValue && preservedValues.has(paletteValue)) {
                    return paletteValue;
                }

                return emptyPaletteSlotValue(index);
            });

            this.syncMasterPaletteSelectedValues(preservedValues);
        },

        clearStepSubOptionGroupColorSelections(groupKeys = []) {
            const nextStepSubOptions = {
                ...this.uiStepSubOptions,
            };
            const nextActiveByOptionName = {
                ...(this.uiStepSubOptions?.activePaletteSlotByOptionName || {}),
            };

            groupKeys.forEach((groupKey) => {
                const options = Array.isArray(this.uiStepSubOptions?.[groupKey])
                    ? this.uiStepSubOptions[groupKey]
                    : [];

                nextStepSubOptions[groupKey] = options.map((option) => {
                    delete nextActiveByOptionName[option.name];

                    return {
                        ...option,
                        selectedColor: null,
                    };
                });
            });

            this.uiStepSubOptions = {
                ...nextStepSubOptions,
                activePaletteSlotByOptionName: nextActiveByOptionName,
            };
            this.uiPendingStepSubOptionColorChange = null;
            this.prunePaintfillSelectionOrder();
        },

        setStepSubOptionActivePaletteSlot(optionName, activeSlotInput) {
            const targetOption = findStepSubOption(this.uiStepSubOptions, optionName);

            if (!targetOption) {
                return;
            }

            const nextValue = normalizeStepSubOptionActiveSlotInput(activeSlotInput) || null;

            this.uiStepSubOptions.activePaletteSlotByOptionName = {
                ...(this.uiStepSubOptions.activePaletteSlotByOptionName || {}),
                [optionName]: nextValue,
            };

            if (!nextValue) {
                return;
            }

            targetOption.selectedColor = selectedColorFromActivePaletteSlot(
                this.masterPalette,
                nextValue,
            );

            this.syncColorWithSlot(optionName, targetOption.selectedColor);
        },

        syncColorWithSlot(optionName, selectedColor) {
            // eslint-disable-next-line
            console.log(`[Paint] Setting ${optionName} to ${selectedColor}`);

            const paintfillGroupName = paintfillGroupNameForPlacementCode(optionName);

            if (paintfillGroupName && selectedColor != null) {
                this.markPaintfillSectionAdded(paintfillGroupName);
            }

            // Sync colors with controller.
            // And commit the selection to controller.
            const placement = this.viewController.findPlacement(optionName);
            if (placement != null && selectedColor != null) {
                for (const c of placement.components) {
                    if (c.placeholder) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    // TODO: We should figure out how to match by codes, not hexes.
                    const hex = c.custom['v-hex'];

                    if (hex?.toLowerCase() === selectedColor.toLowerCase()) {
                        this.viewController.updateComponent(placement.code, c.code);
                        break;
                    }
                }
            }
        },

        setStepSubOptionGroupOptions(groupKey, optionNames = []) {
            const existingOptions = Array.isArray(this.uiStepSubOptions?.[groupKey])
                ? this.uiStepSubOptions[groupKey]
                : [];
            const nextNames = Array.from(new Set(
                (Array.isArray(optionNames) ? optionNames : [])
                    .map((name) => (name || '').trim())
                    .filter((name) => name.length > 0),
            ));
            const existingByName = new Map(existingOptions.map((option) => [option.name, option]));

            const nextOptions = nextNames.map((name) => {
                const existing = existingByName.get(name);
                const activeSlotValue = this.getStepSubOptionActivePaletteSlot(name);
                const selectedColor = selectedColorFromActivePaletteSlot(this.masterPalette, activeSlotValue);
                const selectedComponent = this.viewController?.selectedAtPlacement(name);
                const controllerColor = selectedComponent && !selectedComponent.placeholder
                    ? selectedComponent.custom?.['v-hex']
                    : null;

                return {
                    name,
                    selectedColor: selectedColor ?? existing?.selectedColor ?? controllerColor ?? null,
                };
            });

            const nextActiveByOptionName = {
                ...(this.uiStepSubOptions?.activePaletteSlotByOptionName || {}),
            };

            existingOptions.forEach((option) => {
                if (!nextNames.includes(option.name)) {
                    delete nextActiveByOptionName[option.name];
                }
            });

            this.uiStepSubOptions = {
                ...this.uiStepSubOptions,
                [groupKey]: nextOptions,
                activePaletteSlotByOptionName: nextActiveByOptionName,
            };
        },

        syncFrontInitialsSubOptionsFromPlacements(placements) {
            const names = (Array.isArray(placements) ? placements : [])
                .filter((placement) => placement?.code?.includes('Color'))
                // Prefer codes.
                .map((placement) => placement?.code);

            this.setStepSubOptionGroupOptions('frontInitials', names);
        },

        syncFrontStampsSubOptionsFromPlacements(placements) {
            const names = (Array.isArray(placements) ? placements : [])
                .filter((placement) => placement?.code?.includes('Color'))
                // Prefer codes.
                .map((placement) => placement?.code);

            this.setStepSubOptionGroupOptions('frontStamps', names);
        },

        getStepSubOptionActivePaletteSlot(optionName) {
            if (!optionName) {
                return null;
            }

            return this.uiStepSubOptions
                ?.activePaletteSlotByOptionName
                ?.[optionName] || null;
        },

        swapStepSubOptionAssignmentsByActiveSlotValue(fromValue, toValue, swapExistingAssignments = false) {
            if (!fromValue || !toValue) {
                return;
            }

            const nextByOptionName = {
                ...(this.uiStepSubOptions?.activePaletteSlotByOptionName || {}),
            };

            stepSubOptionCollections(this.uiStepSubOptions).forEach((options) => {
                options.forEach((option) => {
                    const current = nextByOptionName[option.name];
                    let next = current;

                    if (current === fromValue) {
                        next = toValue;
                    } else if (swapExistingAssignments && current === toValue) {
                        next = fromValue;
                    }

                    if (next !== current) {
                        nextByOptionName[option.name] = next;
                        option.selectedColor = selectedColorFromActivePaletteSlot(this.masterPalette, next);

                        this.syncColorWithSlot(option.name, option.selectedColor);
                    }
                });
            });

            this.uiStepSubOptions.activePaletteSlotByOptionName = nextByOptionName;
        },

        applyPaletteSlotColorChangeAndAssignments(fromValue, toValue) {
            if (!fromValue || !toValue) {
                return null;
            }

            const shouldSwapExistingAssignments = this.paletteSwatchSlots.includes(toValue)
                && fromValue !== toValue
                && !isEmptyPaletteSlotValue(fromValue);

            const nextActiveSlotValue = this.applyColorToPaletteSwatchSlot(fromValue, toValue);

            if (!nextActiveSlotValue) {
                return null;
            }

            this.swapStepSubOptionAssignmentsByActiveSlotValue(
                fromValue,
                nextActiveSlotValue,
                shouldSwapExistingAssignments,
            );

            return nextActiveSlotValue;
        },

        applyColorToNextOpenPaletteSwatchSlot(paletteValue) {
            const nextPaletteValue = normalizePaletteSelectionValue(this.masterPalette, paletteValue);

            if (!nextPaletteValue || !Array.isArray(this.paletteSwatchSlots)) {
                return null;
            }

            if (this.paletteSwatchSlots.includes(nextPaletteValue)) {
                return nextPaletteValue;
            }

            const nextOpenSlotValue = this.paletteSwatchSlots
                .find((slotValue) => isEmptyPaletteSlotValue(slotValue));

            if (!nextOpenSlotValue) {
                return null;
            }

            return this.applyColorToPaletteSwatchSlot(nextOpenSlotValue, nextPaletteValue);
        },

        requestFullPaletteColorChange(optionName, nextPaletteValue) {
            const replacementSlotValue = this.paletteSwatchSlots
                .find((slotValue) => !isEmptyPaletteSlotValue(slotValue));

            if (!replacementSlotValue) {
                return null;
            }

            this.uiPendingStepSubOptionColorChange = {
                optionName,
                fromValue: replacementSlotValue,
                toValue: nextPaletteValue,
            };

            const appStore = useAppStore();
            appStore.openNestedPanel('colorChangeConfirm', this.getColorChangeConfirmPayload({
                source: 'fullPaletteStepSubOptionColorChange',
                optionName,
            }));

            return null;
        },

        updatePendingStepSubOptionColorChangeFromValue(fromValue) {
            if (!this.uiPendingStepSubOptionColorChange || !fromValue) {
                return;
            }

            const nextFromValue = normalizePaletteSelectionValue(this.masterPalette, fromValue);

            if (!nextFromValue || !this.paletteSwatchSlots.includes(nextFromValue)) {
                return;
            }

            this.uiPendingStepSubOptionColorChange = {
                ...this.uiPendingStepSubOptionColorChange,
                fromValue: nextFromValue,
            };
        },

        requestPaletteSlotColorChange(activeSlotValue, paletteSelection, meta = {}) {
            if (!activeSlotValue) {
                return null;
            }

            const nextPaletteValue = normalizePaletteSelectionValue(this.masterPalette, paletteSelection);

            if (!nextPaletteValue) {
                return null;
            }

            const isChangingAssignedSlot = !isEmptyPaletteSlotValue(activeSlotValue)
                && activeSlotValue !== nextPaletteValue;
            const affectedAssignments = buildStepSubOptionAssignments(this.uiStepSubOptions, activeSlotValue);
            const shouldRequireConfirmation = isChangingAssignedSlot
                && affectedAssignments.length >= 2;

            if (!shouldRequireConfirmation) {
                return this.applyPaletteSlotColorChangeAndAssignments(activeSlotValue, nextPaletteValue);
            }

            this.uiPendingStepSubOptionColorChange = {
                optionName: meta?.optionName || null,
                fromValue: activeSlotValue,
                toValue: nextPaletteValue,
            };

            const appStore = useAppStore();
            appStore.openNestedPanel('colorChangeConfirm', this.getColorChangeConfirmPayload({
                source: meta?.source || 'paletteSlotColorChange',
                optionName: meta?.optionName || null,
            }));

            return null;
        },

        requestStepSubOptionColorChange(optionName, paletteSelection) {
            const targetOption = findStepSubOption(this.uiStepSubOptions, optionName);

            if (!targetOption) {
                return null;
            }

            const nextPaletteValue = normalizePaletteSelectionValue(this.masterPalette, paletteSelection);

            if (!nextPaletteValue) {
                return null;
            }

            if (paletteSelection?.raw || !this.isAnyPaintfillSelected()) {
                // Apply to the item only.
                this.setStepSubOptionActivePaletteSlot(optionName, nextPaletteValue);
                return nextPaletteValue;
            }

            if (this.paletteSwatchSlots.includes(nextPaletteValue)) {
                this.setStepSubOptionActivePaletteSlot(optionName, nextPaletteValue);
                return nextPaletteValue;
            }

            const nextActiveSlotValue = this.applyColorToNextOpenPaletteSwatchSlot(nextPaletteValue);

            if (!nextActiveSlotValue) {
                return this.requestFullPaletteColorChange(optionName, nextPaletteValue);
            }

            this.setStepSubOptionActivePaletteSlot(optionName, nextActiveSlotValue);
            return nextActiveSlotValue;
        },

        confirmStepSubOptionColorChange({ isReplacement }) {
            const pending = this.uiPendingStepSubOptionColorChange;

            if (!pending) {
                return;
            }

            const { optionName, fromValue, toValue } = pending;
            const affectedAssignments = buildStepSubOptionAssignments(this.uiStepSubOptions, fromValue);
            const toastMessage = buildColorChangeToastMessage(
                this.masterPalette,
                toValue,
                affectedAssignments.length,
                isReplacement,
            );
            const toastMessageA = toastMessage.split(':')[0].concat(': ');
            const toastMessageB = toastMessage.split(':')[1];

            const nextActiveSlotValue = this.applyPaletteSlotColorChangeAndAssignments(fromValue, toValue);

            if (optionName && nextActiveSlotValue) {
                this.setStepSubOptionActivePaletteSlot(optionName, nextActiveSlotValue);
            }

            this.uiPendingStepSubOptionColorChange = null;
            const appStore = useAppStore();
            appStore.closeNestedPanel();
            appStore.showToast({
                anchor: 'top',
                message: toastMessageA,
                messageB: toastMessageB,
                hexColor: toValue,
                duration: 3000,
            });
        },

        cancelStepSubOptionColorChange() {
            this.uiPendingStepSubOptionColorChange = null;
            const appStore = useAppStore();
            appStore.closeNestedPanel();
        },

        getColorChangeConfirmPayload(extraPayload = null) {
            const activePaletteSlotByOptionName = {
                ...(this.uiStepSubOptions?.activePaletteSlotByOptionName || {}),
            };
            const pendingColorChange = this.uiPendingStepSubOptionColorChange;
            const pendingAssignments = pendingColorChange
                ? buildStepSubOptionAssignments(this.uiStepSubOptions, pendingColorChange.fromValue)
                : [];

            const top = buildStepSubOptionPayloadList(
                this.uiStepSubOptions?.top,
                activePaletteSlotByOptionName,
                this.masterPalette,
            );
            const backLogo = buildStepSubOptionPayloadList(
                this.uiStepSubOptions?.backLogo,
                activePaletteSlotByOptionName,
                this.masterPalette,
            );
            const backCavityDots = buildStepSubOptionPayloadList(
                this.uiStepSubOptions?.backCavityDots,
                activePaletteSlotByOptionName,
                this.masterPalette,
            );
            const frontPaintfillWeights = buildStepSubOptionPayloadList(
                this.uiStepSubOptions?.frontPaintfillWeights,
                activePaletteSlotByOptionName,
                this.masterPalette,
            );
            const frontPaintfillLogos = buildStepSubOptionPayloadList(
                this.uiStepSubOptions?.frontPaintfillLogos,
                activePaletteSlotByOptionName,
                this.masterPalette,
            );
            const frontInitials = buildStepSubOptionPayloadList(
                this.uiStepSubOptions?.frontInitials,
                activePaletteSlotByOptionName,
                this.masterPalette,
            );
            const frontStamps = buildStepSubOptionPayloadList(
                this.uiStepSubOptions?.frontStamps,
                activePaletteSlotByOptionName,
                this.masterPalette,
            );

            return {
                top,
                backLogo,
                backCavityDots,
                frontPaintfillWeights,
                frontPaintfillLogos,
                frontInitials,
                frontStamps,
                options: [
                    ...top,
                    ...backLogo,
                    ...backCavityDots,
                    ...frontPaintfillWeights,
                    ...frontPaintfillLogos,
                    ...frontInitials,
                    ...frontStamps,
                ],
                pendingColorChange: pendingColorChange
                    ? {
                        ...pendingColorChange,
                        assignments: pendingAssignments,
                        fromColor: selectedColorFromActivePaletteSlot(this.masterPalette, pendingColorChange.fromValue),
                        fromName: colorLabelFromPaletteValue(this.masterPalette, pendingColorChange.fromValue),
                        toColor: selectedColorFromActivePaletteSlot(this.masterPalette, pendingColorChange.toValue),
                        toName: colorLabelFromPaletteValue(this.masterPalette, pendingColorChange.toValue),
                    }
                    : null,
                activePaletteSlotByOptionName,
                meta: extraPayload,
            };
        },

        getAllColorLocationsPayload(extraPayload = null) {
            const groups = buildAllColorLocationGroups(
                this.uiStepSubOptions,
                this.masterPalette,
            );

            return {
                groups,
                options: groups.flatMap((group) => group.options),
                meta: extraPayload,
            };
        },

        openAllColorLocationsPanel(extraPayload = null) {
            const appStore = useAppStore();
            appStore.openNestedPanel(
                'allColorLocations',
                this.getAllColorLocationsPayload(extraPayload),
            );
        },

        setPaletteSwatchSlots(slots) {
            if (!Array.isArray(slots)) {
                return;
            }

            this.paletteSwatchSlots = Array.from({ length: PALETTE_SWATCH_SLOT_COUNT }, (_, index) => {
                const slotValue = slots[index];

                if (typeof slotValue === 'string' && slotValue.length > 0) {
                    return slotValue;
                }

                return emptyPaletteSlotValue(index);
            });
        },

        applyColorToPaletteSwatchSlot(activeSlotValue, paletteValue) {
            if (!activeSlotValue || !paletteValue || !Array.isArray(this.paletteSwatchSlots)) {
                return null;
            }

            const directMatch = this.masterPalette.find((option) => option.value === paletteValue);
            const colorMatch = this.masterPalette.find((option) => option.color === paletteValue);
            const nextValue = directMatch?.value || colorMatch?.value || null;

            if (!nextValue) {
                return null;
            }

            const activeIndex = this.paletteSwatchSlots.findIndex((slotValue) => slotValue === activeSlotValue);

            if (activeIndex === -1) {
                return null;
            }

            const nextSlots = [...this.paletteSwatchSlots];
            const currentSlotValue = nextSlots[activeIndex];
            const currentSlotIsEmpty = isEmptyPaletteSlotValue(currentSlotValue);

            if (currentSlotValue === nextValue) {
                return nextValue;
            }

            const existingIndex = nextSlots.findIndex((slotValue) => slotValue === nextValue);

            if (existingIndex !== -1) {
                nextSlots[activeIndex] = nextValue;
                nextSlots[existingIndex] = currentSlotIsEmpty
                    ? emptyPaletteSlotValue(existingIndex)
                    : currentSlotValue;
                this.setPaletteSwatchSlots(nextSlots);
                return nextValue;
            }

            if (!currentSlotIsEmpty) {
                this.setColorSelected(currentSlotValue, false);
            }

            this.setColorSelected(nextValue, true);
            nextSlots[activeIndex] = nextValue;
            this.setPaletteSwatchSlots(nextSlots);
            return nextValue;
        },

        /**
         * Set the selected color in the master palette
         */
        setColorSelected(value, selected = null) {
            if (!value || !Array.isArray(this.masterPalette)) {
                return;
            }

            const target = this.masterPalette.find((item) => item.value === value);

            if (!target) {
                return;
            }

            target.selected = selected == null ? !target.selected : !!selected;
        },

        /**
         * Persist UI: active state for Front subsections
         */
        setFrontSectionActive(section, active = true) {
            if (!FRONT_NESTED_PANEL_SECTIONS.includes(section)) {
                return;
            }

            this.uiFrontActiveSections = {
                ...this.uiFrontActiveSections,
                [section]: !!active,
            };
        },

        setFrontNestedPanelValidationAttempted(section, attempted = true) {
            if (!FRONT_NESTED_PANEL_SECTIONS.includes(section)) {
                return;
            }

            this.uiFrontNestedPanelValidationAttempted = {
                ...this.uiFrontNestedPanelValidationAttempted,
                [section]: !!attempted,
            };
        },

        hasFrontNestedPanelValidationAttempted(section) {
            if (!FRONT_NESTED_PANEL_SECTIONS.includes(section)) {
                return false;
            }

            return !!this.uiFrontNestedPanelValidationAttempted?.[section];
        },

        clearFrontNestedPanelValidationAttempted(section = null) {
            if (!section) {
                this.uiFrontNestedPanelValidationAttempted = {};
                return;
            }

            this.setFrontNestedPanelValidationAttempted(section, false);
        },

        /**
         * Checks if any paintfill is selected for front, top, and back.
         * @return {boolean} True if any paintfill is selected, false otherwise.
         */
        isAnyPaintfillSelected(groupName = null) {
            if (groupName) {
                return this.isPaintfillMainSelectedForGroup(groupName);
            }

            return Object.keys(PAINTFILL_GROUPS)
                .some((name) => this.isPaintfillMainSelectedForGroup(name));
        },

        isTopPaintfillComplete() {
            return this.isPaintfillComplete('Top');
        },

        isBackPaintfillComplete() {
            return this.isPaintfillComplete('Back');
        },

        isFrontPaintfillComplete() {
            return this.isPaintfillComplete('Face/Sole');
        },

        isPaintfillComplete(groupName = null) {
            if (groupName) {
                const placementCodes = this.paintfillColorPlacementCodesForGroup(groupName);

                if (placementCodes.length === 0 || !this.isPaintfillMainSelectedForGroup(groupName)) {
                    return false;
                }

                return placementCodes.every((placementCode) => this.isColorPlacementSelected(placementCode));
            }

            return this.isAnyPaintfillSelected()
                && this.isTopPaintfillComplete()
                && this.isBackPaintfillComplete()
                && this.isFrontPaintfillComplete();
        },

        isPaintfillMainSelectedForGroup(groupName) {
            const normalizedGroupName = normalizePaintfillGroupName(groupName);
            const placementCode = PAINTFILL_GROUPS[normalizedGroupName]?.mainPlacement;

            return this.isPlacementSelected(placementCode);
        },

        paintfillColorPlacementCodesForGroup(groupName) {
            const normalizedGroupName = normalizePaintfillGroupName(groupName);
            const previewPlacementCodes = new Set(
                PREVIEW_IMAGE_PAINTFILL_COLOR_PLACEMENTS[normalizedGroupName] || [],
            );
            const subOptionKeys = this.paintfillSubOptionKeysForGroup(normalizedGroupName);

            return subOptionKeys.flatMap((key) => (
                this.uiStepSubOptions?.[key] || []
            ).map((option) => option.name).filter((name) => previewPlacementCodes.has(name)));
        },

        paintfillSubOptionKeysForGroup(groupName) {
            const normalizedGroupName = normalizePaintfillGroupName(groupName);
            const previewPlacementCodes = new Set(
                PREVIEW_IMAGE_PAINTFILL_COLOR_PLACEMENTS[normalizedGroupName] || [],
            );

            if (previewPlacementCodes.size === 0) {
                return [];
            }

            return stepSubOptionGroups(this.uiStepSubOptions)
                .filter((group) => group.options
                    .some((option) => previewPlacementCodes.has(option.name)))
                .map((group) => group.key);
        },

        hasIncompletePaintfillForGroup(groupName) {
            return this.isAnyPaintfillSelected()
                && !this.isPaintfillComplete(groupName);
        },

        isPlacementSelected(placementCode) {
            if (!placementCode) {
                return false;
            }

            const selected = this.viewController?.selectedAtPlacement(placementCode);
            const code = selected?.code || '';

            return !!(selected && !selected.placeholder
                && !code.includes('DefaultColors')
                && !code.includes('Blank'));
        },

        isColorPlacementSelected(placementCode) {
            if (!placementCode) {
                return false;
            }

            const selected = this.viewController?.selectedAtPlacement(placementCode);

            return !!(selected && !selected.placeholder && selected.custom?.['v-hex']);
        },

        isFrontSectionActive(section) {
            if (section === 'paintfill') {
                const selected = this.viewController?.selectedAtPlacement('PutterFrontPaintfill');

                return selected && !selected.placeholder;
            }

            if (section === 'stamps') {
                const selected = this.viewController?.selectedAtPlacement('Stamp');

                return selected && !selected.placeholder;
            }

            return !!this.uiFrontActiveSections?.[section];
        },

        isTopSectionActive() {
            const selected = this.viewController?.selectedAtPlacement('PutterTopPaintfill');

            return selected && !selected.placeholder;
        },

        isBackSectionActive() {
            const selected = this.viewController?.selectedAtPlacement('PutterBackPaintfill');

            return selected && !selected.placeholder;
        },

        clearFrontActiveSection(section = null) {
            if (!section) {
                this.uiFrontActiveSections = {};
                return;
            }

            this.setFrontSectionActive(section, false);
        },

        /**
         * Persist UI: Front -> No Customization selected state
         */
        setFrontNoCustomizationSelected(groupName, selected) {
            if (!groupName) {
                return;
            }

            this.uiFrontNoCustomizationSelected = {
                ...this.uiFrontNoCustomizationSelected,
                [groupName]: !!selected,
            };
        },

        isFrontNoCustomizationSelected(groupName) {
            if (!groupName) {
                return false;
            }

            return !!this.uiFrontNoCustomizationSelected?.[groupName];
        },

        /**
         * Persist UI: Top -> No Customization selected state
         */
        setTopNoCustomizationSelected(selected) {
            this.uiTopNoCustomizationSelected = !!selected;
        },

        isTopNoCustomizationSelected() {
            return !!this.uiTopNoCustomizationSelected;
        },

        /**
         * Persist UI: Back -> No Customization selected state
         */
        setBackNoCustomizationSelected(selected) {
            this.uiBackNoCustomizationSelected = !!selected;
        },

        isBackNoCustomizationSelected() {
            return !!this.uiBackNoCustomizationSelected;
        },

        restoreNoCustomizationSelections(controller, recipe) {
            const recipePlacementCodes = new Set(
                (recipe?.components || []).map((selection) => selection.code),
            );
            const isOptionalPlaceholderSelected = (placementCode) => {
                if (!recipePlacementCodes.has(placementCode)) {
                    return false;
                }

                const selected = controller?.selectedAtPlacement(placementCode);

                return !!(selected?.placeholder && selected?.placeholderOptional);
            };
            const initials = controller?.selectedAtPlacement('StampingStyle');
            const stamps = controller?.selectedAtPlacement('Stamp');
            const hasInitials = !!(initials && !initials.placeholder);
            const hasStamps = !!(stamps
                && !stamps.placeholder
                && stamps.code !== 'Stamp-Stamp-Blank');

            this.setFrontNoCustomizationSelected(
                'Face/Sole',
                isOptionalPlaceholderSelected('PutterFrontPaintfill')
                    && !hasInitials
                    && !hasStamps,
            );
            this.setTopNoCustomizationSelected(
                isOptionalPlaceholderSelected('PutterTopPaintfill'),
            );
            this.setBackNoCustomizationSelected(
                isOptionalPlaceholderSelected('PutterBackPaintfill'),
            );
        },

        buildVisibleGroups({ sourceGroups } = {}) {
            const originalGroups = Array.isArray(sourceGroups) ? sourceGroups : [];

            const groupHasOptions = (group) => {
                if (group?.name === 'Shaft' || group?.code === 'Shaft') {
                    return this.hasPlacementOptions('Shaft', 'Shaft');
                }

                return true;
            };

            const stampsIndex = originalGroups.findIndex((group) => group.code === 'Stamps');
            const characterStampingIndex = originalGroups.findIndex((group) => group.code === 'CharacterStamping');
            const paintfillIndex = originalGroups.findIndex((group) => group.code === 'Paintfill');

            const stampsGroup = originalGroups[stampsIndex];
            const characterStampingGroup = originalGroups[characterStampingIndex];

            let frontGroup = null;

            if (stampsIndex >= 0 || characterStampingIndex >= 0 || paintfillIndex > 0) {
                frontGroup = {
                    name: 'Face/Sole',
                    code: 'Face/Sole',
                    custom: {
                        'v-group-type': 'front',
                    },
                    placements: [],
                };

                if (characterStampingGroup) {
                    frontGroup.placements.push(...(characterStampingGroup?.placements || []));
                }

                if (stampsGroup) {
                    frontGroup.placements.push(...(stampsGroup?.placements || []));
                }
            }

            let groups = [
                originalGroups.find((group) => group.code === 'Specifications'),
                originalGroups.find((group) => group.code === 'Shaft'),
                frontGroup,
                originalGroups.find((group) => group.code === 'TopPaintfill'),
                originalGroups.find((group) => group.code === 'BackPaintfill'),
                originalGroups.find((group) => group.code === 'Grip'),
                originalGroups.find((group) => group.code === 'Headcover'),
                originalGroups.find((group) => group.code === 'PutterShaftBand'),
            ].filter(Boolean);

            // Only keep non-hidden.
            groups = groups.filter((x) => x?.custom?.['v-hidden'] !== 'true');

            return groups.filter(groupHasOptions);
        },
        /**
         * Save options.
         */
        setOptions(options) {
            // eslint-disable-next-line
            console.log('Launching with', options);

            this.options = options;

            // Replace empty values that we are getting from SFCC with proper nulls.
            if (this.options.products === '') {
                this.options.products = null;
            }

            if (this.options.package === '') {
                this.options.package = null;
            }
        },

        isRecipe(str) {
            const regex = /^\d+-[A-Za-z0-9]{16}$/;

            return regex.test(str);
        },

        getBaseController() {
            return new Promise((resolve) => {
                const baseController = new this.options.DriveCustomizer.Controller({
                    apiKey: this.options.apiKey,
                    products: ['PutterBaseProduct'],
                });

                baseController.on('afterInitializeBlueprint', () => {
                    resolve(baseController);
                });

                baseController.load();
            });
        },

        /**
         * Start loading products.
         */
        async load(options) {
            this.resetGroupLocks();

            if (options) {
                Object.assign(this.options, options);
            }

            if (this.options.recipe != null && this.options.recipe.length > 0) {
                if (!this.isRecipe(this.options.recipe)) {
                    this.options.products = [this.options.recipe];
                    this.options.recipe = null;
                }
            }

            const baseController = await this.getBaseController();

            // Load reference data.
            this.defaultColors = await baseController.queryDataGrid({
                from: 'PUTTERS_PAINTFILLS_DEFAULT_COLOR',
                take: 1000,
                skip: 0,
            });

            // Instead of checking for this.options.lineItemId which could be missing (in case of wishlist),
            // check for the initial configuration data.
            if (this.options.configuration) {
                // We need OCAPI settings first...
                const controller = new this.options.DriveCustomizer.Controller({
                    apiKey: this.options.apiKey,
                    products: ['PutterBaseProduct'],
                });

                const blueprintUrl = controller.resolveBlueprintUrl();

                fetch(blueprintUrl).then((response) => response.json()).then(() => {
                    if (this.options.configuration) {
                        let data = null;
                        if (Array.isArray(this.options.configuration)) {
                            const first = this.options.configuration[0];

                            if (first.payload) {
                                data = first.payload;
                            }
                        } else if (this.options.configuration.recipe) {
                            data = this.options.configuration;
                        }

                        // And if there is no data?
                        this.loadFromData(data);
                    }
                });
            } else if (this.options.recipe) {
                // Load from recipe.
                const recipeUrl = `https://cz.drrv.co/recipe/${this.options.recipe}.json`;
                const fallbackRecipeUrl = `https://dev.cz.drrv.co/recipe/${this.options.recipe}.json`;

                const fetchRecipe = async () => {
                    try {
                        const response = await fetch(recipeUrl);

                        if (!response.ok) {
                            throw new Error(`Primary fetch failed: ${response.status}`);
                        }

                        return await response.json();
                    } catch (error) {
                        const response = await fetch(fallbackRecipeUrl);

                        if (!response.ok) {
                            throw new Error(`Fallback fetch failed: ${response.status}`);
                        }

                        // eslint-disable-next-line no-return-await
                        return await response.json();
                    }
                };

                fetchRecipe().then((data) => {
                    this.initialRecipe = data;

                    this.options.products = [data.styleCode];

                    this.initialData = {};

                    this.initialData.item = data.styleCode;

                    // Continue to load.
                    this.loadCustomizeFlow();
                });
            } else {
                // Continue loading the controller.
                this.loadCustomizeFlow();
            }
        },

        /**
         * Load from configuration data.
         */
        loadFromData(config) {
            this.initialData = config;

            if (this.initialData.recipe) {
                const recipe = this.initialData.recipe;

                const recipeUrl = `https://cz.drrv.co/recipe/${recipe}.json`;
                const fallbackRecipeUrl = `https://customizerstoragenightly.blob.core.windows.net/recipe/${recipe}.json`;

                fetch(recipeUrl)
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error(`Production fetch failed: ${response.status}`);
                        }
                        return response.json();
                    })
                    // eslint-disable-next-line arrow-body-style
                    .catch(() => {
                        return fetch(fallbackRecipeUrl).then((response) => {
                            if (!response.ok) {
                                throw new Error(`Fallback fetch failed: ${response.status}`);
                            }

                            return response.json();
                        });
                    })
                    .then((data) => {
                        this.initialRecipe = data;

                        this.options.products = [config.item];

                        // Continue to load.
                        this.loadCustomizeFlow();
                    });
            }
        },

        /**
         * Calculate default controller options.
         */
        defaultControllerOptions() {
            let useSite = this.options.site;

            if (useSite == null || useSite === '') {
                if (this.options.locale) {
                    const locale = this.options.locale.toLowerCase().replace('-', '').replace('_', '');

                    if (locale === 'enus') {
                        useSite = 'SFCC-US';
                    }

                    if (locale === 'enca') {
                        useSite = 'SFCC-CA';
                    }

                    if (locale === 'frca') {
                        useSite = 'SFCC-CA';
                    }
                }
            }

            return {
                apiKey: this.options.apiKey,
                products: this.options.products,
                preview: this.options.preview,
                site: useSite,
                version: this.options.version,
                locale: this.options.locale,
                disableCaching: this.options.disableCaching,
            };
        },

        /**
         * Loads and availability for components mentioned in the blueprint.
         */
        // { blueprint } parameter is not yet used in this version.
        initializeAvailability() {
            return new Promise((resolve) => {
                resolve();
            });
        },

        priceFormatted(v, relative) {
            if (v == null) {
                return '';
            }

            if (+v === 0) {
                return '';
            }

            const locale = this.options.locale ?? 'en-US';
            const currency = this.options.currency ?? 'USD';

            return formatPrice(v, relative, locale, currency);
        },

        /**
         * Retrieve a component price.
         */
        // eslint-disable-next-line no-unused-vars
        getPrice(controller, component, type) {
            const currency = this.options.currency ?? 'USD';

            // Check for Surefit hub overrides.
            const override = this.getSurefitHubOverride(component);
            if (override) {
                return override.value;
            }

            const placement = controller.findComponentPlacement(component.code);

            if (placement) {
                const agsCode = component.custom?.['v-ags-code'];

                const overridePrice = placement.custom?.[`v-${agsCode}-price-${currency}`];

                if (overridePrice) {
                    if (type === 'basePrice') {
                        return overridePrice;
                    }

                    if (overridePrice != null) {
                        let overrideAddonPrice = null;

                        if (overridePrice != null) {
                            // Also check for addons.
                            overrideAddonPrice = placement.custom?.[`v-${agsCode}-symbols-addon-price-${currency}`];

                            if (overrideAddonPrice != null) {
                                if (type === 'addonPrice') {
                                    return overrideAddonPrice;
                                }

                                // Is there a symbol selected.
                                const symbol = controller.selectedAtPlacement('StampSymbols');
                                if (symbol && symbol.placeholder) {
                                    overrideAddonPrice = null;
                                }
                            }
                        } else if (type === 'addonPrice') {
                            return null;
                        }

                        if (type == null) {
                            return (+overridePrice) + (+(overrideAddonPrice ?? 0));
                        }
                    }
                }
            }

            // Product level overrides.
            if (component?.description?.[0]?.definition === 'Putter' && this.options.pricingOverride?.hideTotalPricing) {
                return null;
            }

            const price = this.getOptionPriceValue(controller, component);

            if (price) {
                return price.value;
            }

            return undefined;
        },

        getSurefitHubOverrideCategory(option, mapCategory, soloCategory) {
            // Check for Surefit hub overrides.
            if (option?.placementIndependentCode) {
                if (this.options.pricingOverride?.[mapCategory]?.[option.placementIndependentCode]) {
                    return {
                        value: this.options.pricingOverride?.[mapCategory]?.[option.placementIndependentCode],
                    };
                }

                if (option.custom['v-id']) {
                    if (this.options.pricingOverride?.[mapCategory]?.[option.custom['v-id']]) {
                        return {
                            value: this.options.pricingOverride?.[mapCategory]?.[option.custom['v-id']],
                        };
                    }
                }

                if (option.custom['v-parent-id']) {
                    if (this.options.pricingOverride?.[mapCategory]?.[option.custom['v-parent-id']]) {
                        return {
                            value: this.options.pricingOverride?.[mapCategory]?.[option.custom['v-parent-id']],
                        };
                    }
                }
            }

            if (this.options.pricingOverride != null) {
                if (option.custom['v-custom-shop'] === 'false') {
                    return {
                        value: 0,
                    };
                }
            }

            if (this.options.pricingOverride?.[soloCategory]) {
                return {
                    value: this.options.pricingOverride?.[soloCategory],
                };
            }

            return null;
        },

        getSurefitHubOverride(option) {
            let override = null;

            if (option?.description?.[0]?.definition === 'Shaft') {
                override = this.getSurefitHubOverrideCategory(option, 'shaftPrices', 'shafts');
                if (override) {
                    return override;
                }
            }

            if (option?.description?.[0]?.definition === 'Grip') {
                override = this.getSurefitHubOverrideCategory(option, 'gripPrices', 'grips');
                if (override) {
                    return override;
                }
            }

            if (option?.description?.[0]?.definition === 'PutterShaftBand') {
                override = this.getSurefitHubOverrideCategory(option, 'shaftBandPrices', 'customShaftBand');
                if (override) {
                    return override;
                }
            }

            if (option?.description?.[0]?.definition === 'Headcover') {
                override = this.getSurefitHubOverrideCategory(option, 'headcoverPrices', 'headcover');
                if (override) {
                    return override;
                }
            }

            if (option?.description?.[0]?.definition === 'Stamp') {
                override = this.getSurefitHubOverrideCategory(option, 'stamps', 'stamps');
                if (override) {
                    return override;
                }
            }

            if (option?.code.startsWith('PutterFrontPaintfill')
                || option?.code.startsWith('PutterTopPaintfill')
                || option?.code.startsWith('PutterBackPaintfill')) {
                override = this.getSurefitHubOverrideCategory(option, 'paintFill', 'paintFill');
                if (override) {
                    return override;
                }
            }

            if (option?.code.startsWith('StampingStyle')) {
                override = this.getSurefitHubOverrideCategory(option, 'initialsPrices', 'initials');
                if (override) {
                    return override;
                }
            }

            override = this.getSurefitHubOverrideCategory(option, 'price');
            if (override) {
                return override;
            }

            return null;
        },

        getOptionPriceValue(controller, option) {
            const currency = this.options.currency ?? 'USD';

            const override = this.getSurefitHubOverride(option);
            if (override) {
                return override;
            }

            // Check for product specific overrides.

            // Vokey can have pricing defined just at the wedge level.
            let priceKey = `v-price-${option.placementIndependentCode}-${currency}`;

            if (controller.blueprint.custom[priceKey]) {
                const value = parseFloat(controller.blueprint.custom[priceKey]);

                return { value };
            }

            if (option.custom['v-id']) {
                priceKey = `v-price-${option.custom['v-id']}-${currency}`;

                if (controller.blueprint.custom[priceKey]) {
                    const value = parseFloat(controller.blueprint.custom[priceKey]);

                    return { value };
                }
            }

            if (option.custom['v-parent-id']) {
                priceKey = `v-price-${option.custom['v-parent-id']}-${currency}`;

                if (controller.blueprint.custom[priceKey]) {
                    const value = parseFloat(controller.blueprint.custom[priceKey]);

                    return { value };
                }
            }

            if (option.price == null) {
                return null;
            }

            let price = option.price.find((p) => p.currencyCode === this.currencyCode);

            if (price == null) {
                price = option.price.find((p) => p.currencyCode === currency);
            }

            if (price == null || price.value === 0) {
                return null;
            }

            return price;
        },

        getOptionPrice(controller, option) {
            const currency = this.options.currency ?? 'USD';

            const price = this.getOptionPriceValue(controller, option);

            if (price == null) {
                return price;
            }

            return formatPrice(price.value, true, this.locale, currency);
        },

        /**
         * Attaches events to the controller.
         */
        attachControllerEvents(controller) {
            controller.on('setReactive', (v) => {
                // Vue 3 doesn't need Vue.set anymore.
                v.target[v.key] = v.value;

                return true;
            });

            controller.on('initializeAvailability', (o) => this.initializeAvailability({
                controller: o.controller,
                blueprint: o.blueprint,
            }));

            controller.on('isAvailable', (component) => this.isAvailable(controller, component));

            controller.on('getPrice', (component) => this.getPrice(controller, component));

            controller.on('stateChanged', () => {
                this.stateChanged(true);
            });
        },

        /**
         * Update things on state change.
         */
        stateChanged(trackChanges) {
            if (this.viewController == null) {
                return;
            }

            const currentPrice = this.viewController.statePrice({
                ignoreOptional: true,
            });

            this.currentPrice = currentPrice;

            const locale = this.options.locale ?? 'en-US';
            const currency = this.options.currency ?? 'USD';

            const mainPrice = currentPrice.groups.Specifications;

            if (mainPrice) {
                if (currentPrice.current) {
                    this.currentPriceFormatted = {
                        current: formatPrice(currentPrice.current, false, locale, currency),
                        min: formatPrice(mainPrice.min, false, locale, currency),
                        max: formatPrice(currentPrice.max, false, locale, currency),
                    };
                } else {
                    this.currentPriceFormatted = null;
                }
            }

            if (!areStampsAllowed(this.viewController)) {
                // clear toe engraving if it's not allowed
                // due to character stamping selection
                const toeEngraving = this.viewController.selectedAtPlacement('Stamp');

                if (toeEngraving) {
                    this.viewController.updateComponent('Stamp', 'Stamp-Stamp-Blank');
                }
            }

            this.syncGroupLocksFromSelections();
            this.updatePreviewImage();

            if (this.isFinishedLoading) {
                if (trackChanges) {
                    this.hasChanged = true;
                }
            }

            this.updateLeadTime();
        },

        /**
         * Update lead time.
         */
        updateLeadTime() {
            let baseTime = 0;
            let shaftTime = 0;
            let gripTime = 0;
            let customTime = 0;

            if (this.viewController.blueprint.custom['v-lead-time']) {
                baseTime = +this.viewController.blueprint.custom['v-lead-time'];
            }

            const shaft = this.viewController.selectedAtPlacement('Shaft');
            if (shaft && shaft.custom['v-lead-time']) {
                shaftTime = +shaft.custom['v-lead-time'];
            }

            const grip = this.viewController.selectedAtPlacement('Grip');
            if (grip && grip.custom['v-lead-time']) {
                gripTime = +grip.custom['v-lead-time'];
            }

            // Do we have customizations?
            let hasCustomizations = false;

            const stamp = this.viewController.selectedAtPlacement('Stamp');
            if (stamp && !stamp.placeholder) {
                hasCustomizations = true;
            }

            const paintfill1 = this.viewController.selectedAtPlacement('PutterFrontPaintfill');
            const paintfill2 = this.viewController.selectedAtPlacement('PutterTopPaintfill');
            const paintfill3 = this.viewController.selectedAtPlacement('PutterBackPaintfill');

            if (
                (paintfill1 && !paintfill1.placeholder)
                || (paintfill2 && !paintfill2.placeholder)
                || (paintfill3 && !paintfill3.placeholder)
            ) {
                hasCustomizations = true;
            }

            const stamping = this.viewController.selectedAtPlacement('StampingStyle');
            if (stamping && !stamping.placeholder) {
                hasCustomizations = true;
            }

            if (hasCustomizations) {
                if (this.options.leadTimeCustom && this.options.leadTimeCustom !== null) {
                    customTime = this.options.leadTimeCustom;
                }
            }

            // eslint-disable-next-line
            console.log(`Lead time: B ${baseTime}, S ${shaftTime}, G ${gripTime}, C ${customTime}`);

            this.leadTime = Math.max(+baseTime, +shaftTime, +gripTime) + (+customTime);
        },

        /**
         * Calculates LiquidFire image URL and loads the data if necessary.
         */
        updatePreviewImage() {
            const build = this.getPreviewImageUrl(this.viewController);

            if (this.lastImageUrl !== build) {
                this.lastImageUrl = build;

                this.loadImage(build);
            }
        },

        loadImage(build) {
            const appStore = useAppStore();
            appStore.setIsLoading(true);

            const loader = new Image();

            loader.onload = () => {
                if (this.lastImageUrl === build) {
                    const imageDiv = document.createElement('div');
                    imageDiv.style.backgroundImage = `url("${build}")`;
                    imageDiv.style.backgroundSize = 'contain';
                    imageDiv.style.backgroundPosition = 'center';
                    imageDiv.style.backgroundRepeat = 'no-repeat';
                    imageDiv.style.width = '100%';
                    imageDiv.style.height = '100%';

                    this.lastRenderedImage = imageDiv;

                    appStore.setIsLoading(false);
                }
            };

            loader.src = build;
        },

        setPart(url, name, value) {
            const v = url.find((x) => x.name === name);

            if (v) {
                v.value = value;
            }
        },

        /**
         * Load controller.
         */
        loadController(controller) {
            this.attachControllerEvents(controller);

            controller.load();

            const appStore = useAppStore();
            appStore.setIsBusy(false);
        },

        /**
         * Check if the component is available according to the current product configuration.
         */
        isAvailable(controller, component) {
            if (this.componentLookup && this.componentLookup[component.code] === false) {
                return false;
            }

            if (!component.available) {
                return false;
            }

            return true;
        },

        /**
         * Load customization flow.
         */
        loadCustomizeFlow() {
            const startOptions = {
                ...this.defaultControllerOptions(),
            };

            if (startOptions.products != null && Array.isArray(startOptions.products) && startOptions.products.length > 0) {
                this.initialSet = [...startOptions.products];
            }

            if (startOptions.products != null && Array.isArray(startOptions.products) && startOptions.products.length > 1) {
                startOptions.products = [startOptions.products[0]];
            }

            const controller = new this.options.DriveCustomizer.Controller(startOptions);

            // controller.traceAllowed = true;
            // controller.traceAvailable = true;
            controller.traceUpdated = true;

            controller.on('afterInitializeState', () => {
            });

            controller.on('afterInitializeBlueprint', () => {
                // Is this an empty startup product?
                if (controller.blueprint.custom['t-empty'] === 'true') {
                    this.loadEmpty(controller);
                } else {
                    // Load actual product options.
                    this.loadColors({
                        controllers: [controller],
                    });
                }
            });

            // Load controller.
            this.loadController(controller);
        },

        /**
         * Discover some active product and load that first so we'd have a "shell" of the configuration.
         */
        loadEmpty(controller) {
            this.loadAsEmpty = true;

            const productType = controller.blueprint.custom['t-product-type'];

            controller.getProductsByTags([productType]).then((products) => {
                let candidates = [];

                for (const p of products) {
                    if (p.tags?.includes('t-empty')) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    candidates.push(p);
                }

                let useFinish = this.options.initialFinish;
                if (useFinish == null) {
                    useFinish = controller.blueprint.custom['t-default-finish'];
                }

                const byFinish = candidates.filter((x) => x.custom['t-finish'] === useFinish);
                if (byFinish.length > 0) {
                    candidates = byFinish;
                }

                let useOrientation = this.options.initialOrientation;
                if (useOrientation == null) {
                    useOrientation = controller.blueprint.custom['t-default-orientation'];
                }

                const byOrientation = candidates.filter((x) => x.custom['t-orientation'] === useOrientation);
                if (byOrientation.length > 0) {
                    candidates = byOrientation;
                }

                const firstAvailable = candidates[0];

                // If none are available... then what?
                if (firstAvailable) {
                    this.options.products = [firstAvailable.styleCode];

                    // Restart the loading sequence.
                    this.loadCustomizeFlow();
                }
            });
        },

        /**
         * Continue initializing the controller and configure product colors.
         */
        loadColors({ controllers }) {
            let activeController = null;

            if (controllers.length > 0) {
                // eslint-disable-next-line
                activeController = controllers[0];
            }

            this.updateTranslations(activeController);

            const colorOptions = [];

            for (const s of activeController.state) {
                const placement = s.placement;

                if (placement.components.length <= 0) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                // Do we have colors?
                for (const c of placement.components) {
                    if (c.placeholder) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const definition = c.description[0].definition;

                    if (definition !== 'StampingColor') {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const hex = c.custom['v-hex'];

                    if (hex == null || hex.length === 0) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const translucent = c.custom['v-translucent'] === 'true';
                    const raw = c.custom['v-raw'] === 'true';

                    const exists = colorOptions.find((x) => x.code === c.placementIndependentCode);

                    if (exists) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    colorOptions.push({
                        label: activeController.componentNames(c).join(' '),
                        value: hex,
                        selected: false,
                        color: hex,
                        order: null,
                        placeholder: null,
                        translucent,
                        raw,
                        code: c.placementIndependentCode,
                        id: c.custom['v-id'],
                    });
                }

                this.masterPalette = colorOptions;
            }

            //   {
            //     "label": "Green Paint Fill",
            //     "value": "#309049",
            //     "selected": false,
            //     "color": "#309049",
            //     "order": null,
            //     "placeholder": false
            //   },

            const continueLoading = () => {
                if (activeController) {
                    const recipeToRestore = this.initialData && this.initialRecipe
                        ? this.initialRecipe
                        : null;

                    if (this.startingController == null) {
                        // First time initialization, restore initial selections from WebTop and options.
                        this.setInitialSelections(activeController);
                    }

                    this.showController(activeController);

                    if (recipeToRestore) {
                        this.restoreNoCustomizationSelections(activeController, recipeToRestore);
                    }

                    // Steps.
                    if (this.uiStepSubOptions == null) {
                        this.uiStepSubOptions = this.buildStepSubOptions();
                    }

                    // Check if there are any assemblies that are already loaded, and if not, select something.
                    this.loadDefaultAssemblies();

                    // Present default groups.
                    this.loadDefaultGroups();

                    this.isFinishedLoading = true;
                }
            };

            if (this.allProducts.length === 0) {
                const tag = activeController.blueprint.custom['t-product-type'];

                activeController.getProductsByTags([tag]).then((products) => {
                    this.allProducts = products;

                    if (this.loadAsEmpty) {
                        this.selectedProducts = [];
                    } else if (this.initialData && this.initialData['club-setcomp']?.length > 0) {
                        const set = [];

                        const agsCode = activeController.blueprint.custom['v-ags-code'];
                        const orientation = this.splitFeatureCode(this.initialData.orientation)[1];

                        // eslint-disable-next-line no-restricted-syntax
                        for (const setClub of this.initialData['club-setcomp']) {
                            const loft = this.splitFeatureCode(setClub)[1];

                            const fullClub = `${agsCode}:${orientation}:${loft}`;

                            const product = products.find((p) => p.styleCode === fullClub);

                            if (product) {
                                set.push(product);
                            } else {
                                // eslint-disable-next-line
                                console.log(`${fullClub} is not found and cannot be restored!`);
                            }
                        }

                        this.selectedProducts = set;
                    } else if (this.initialSet && this.initialSet.length > 0) {
                        this.selectedProducts = this.initialSet.map((x) => products.find((p) => p.styleCode === x));
                    } else {
                        this.selectedProducts = [activeController.blueprint.styleCode].map((x) => products.find((p) => p.styleCode === x));
                    }

                    continueLoading();
                });
            } else {
                continueLoading();
            }
        },

        /**
         * Split feature code into feature and value codes.
         */
        splitFeatureCode(v) {
            const parts = v.split(':');

            return parts;
        },

        loadDefaultGroups() {
            // TODO: Only preselect groups that have values.
            const prefilledGroups = [
                'Specifications',
                'Shaft',
                'Grip',
                'Headcover',
                'Paintfill',
                'Flight Lines',
            ];

            this.visibleGroups.forEach((group) => {
                if (prefilledGroups.includes(group.name)) {
                    this.addGroupToSeen(group.name);
                }
            });
        },

        /**
         * Update translations for the current locale from the controller.
         */
        updateTranslations(controller) {
            if (controller.blueprint?.translations) {
                const appStore = useAppStore();

                const locale = this.options.locale || 'en';

                let messages = appStore.translations.getLocaleMessage(locale);

                if (messages == null) {
                    messages = {};
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const k of Object.keys(controller.blueprint.translations)) {
                    messages[k] = controller.blueprint.translations[k];
                }

                appStore.translations.setLocaleMessage(locale, messages);
            }
        },

        clearSelectedProducts() {
            this.selectedProducts = [];
        },

        toggleSelectedProduct(product) {
            const style = product.styleCode;

            if (this.selectedProducts.some((x) => x.styleCode === style)) {
                // Unselect.
                this.selectedProducts = this.selectedProducts.filter((x) => x.styleCode !== style);
            } else {
                // Select.

                // Are we in the cart edit mode?
                let fromCart = false;

                if (this.options.lineItemId) {
                    const trimmed = this.options.lineItemId.trim();

                    if (trimmed.length > 0 && trimmed !== 'wishlist') {
                        fromCart = true;
                    }
                }

                // Only allow a single select when editing from cart.
                if (fromCart) {
                    this.selectedProducts = [];
                }

                this.selectedProducts.push(product);
            }

            this.loadSelectedProducts();
        },

        changeFinish(finish) {
            const updated = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const p of this.selectedProducts) {
                const currentOrientation = p.custom?.['t-orientation'];
                const currentLoft = p.custom?.['t-setcomp'];

                const other = this.allProducts.find((x) => {
                    if (x.custom?.['t-finish'] === finish
                        && x.custom?.['t-orientation'] === currentOrientation
                        && x.custom?.['t-setcomp'] === currentLoft) {
                        return true;
                    }

                    return false;
                });

                if (other) {
                    updated.push(other);
                }
            }

            this.selectedProducts = updated;

            this.loadSelectedProducts();
        },

        changeOrientation(orientation) {
            const updated = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const p of this.selectedProducts) {
                const currentFinish = p.custom?.['t-finish'];
                const currentLoft = p.custom?.['t-setcomp'];

                const other = this.allProducts.find((x) => {
                    if (x.custom?.['t-finish'] === currentFinish
                        && x.custom?.['t-orientation'] === orientation
                        && x.custom?.['t-setcomp'] === currentLoft) {
                        return true;
                    }

                    return false;
                });

                if (other) {
                    updated.push(other);
                }
            }

            this.selectedProducts = updated;

            this.loadSelectedProducts();
        },

        loadSelectedProducts() {
            const products = this.selectedProducts.map((x) => x.styleCode);

            products.sort();

            if (products.length > 0) {
                const first = products[0];

                if (this.viewController.blueprint.styleCode !== first) {
                    return this.reloadController(first).then((controller) => {
                        this.showController(controller);
                    });
                }
            }

            return undefined;
        },

        setDefaultColorsInternal(controller, placement, defaults) {
            if (placement.custom?.['v-feature']) {
                // Is there a default color?
                const defaultColor = defaults.find((x) => `${x.category}_color` === placement.custom?.['v-feature']);

                if (defaultColor) {
                    // Find matching color.
                    const useFor = `v-use-for-${defaultColor.category}-stamping`;

                    let component = placement.components.find((x) => x.custom[useFor] === '1'
                        && (x.custom['v-hfa-code'] ?? '').toUpperCase() === defaultColor.color1.toUpperCase());

                    if (component == null) {
                        component = placement.components.find((x) => x.custom[useFor] === '1'
                            && controller.componentNames(x).join('').toUpperCase() === defaultColor.color1.toUpperCase());
                    }

                    if (component) {
                        controller.updateComponent(placement.code, component.code);
                    }
                }
            }
        },

        /**
         * Use default colors.
         */
        pickDefaultColors(controller) {
            if (controller.blueprint.custom?.['v-default-colors']) {
                const defaults = JSON.parse(controller.blueprint.custom?.['v-default-colors']);

                // eslint-disable-next-line no-restricted-syntax
                for (const s of controller.state) {
                    const { placement } = s;

                    this.setDefaultColorsInternal(controller, placement, defaults);
                }
            }
        },

        /**
         * Select default grips.
         */
        getDefaultGripFromPlacement(controller, placement) {
            if (!placement.custom?.['t-default-grip']) {
                return null;
            }

            // Find a default grip.
            let useGrip = placement.components.find((x) => !x.placeholder && controller.isAvailable(x)
                && x.custom?.['v-parent-id'] === placement.custom?.['t-default-grip']
                && x.custom?.['v-assembly-default'] === 'true'
                && x.custom?.['v-size-default'] === 'true');

            if (useGrip == null) {
                useGrip = placement.components.find((x) => !x.placeholder && controller.isAvailable(x)
                    && x.custom?.['v-parent-id'] === placement.custom?.['t-default-grip']
                    && x.custom?.['v-size-default'] === 'true');
            }

            if (useGrip == null) {
                useGrip = placement.components.find((x) => !x.placeholder && controller.isAvailable(x)
                    && x.custom?.['v-parent-id'] === placement.custom?.['t-default-grip']);
            }

            if (useGrip == null) {
                useGrip = placement.components.find((x) => !x.placeholder && controller.isAvailable(x));
            }

            return useGrip || null;
        },

        /**
         * Select default grips.
         */
        pickDefaultGrips(controller) {
            // eslint-disable-next-line no-restricted-syntax
            for (const group of controller.blueprint.groups) {
                // eslint-disable-next-line no-restricted-syntax
                for (const placement of group.placements) {
                    const useGrip = this.getDefaultGripFromPlacement(controller, placement);

                    if (useGrip) {
                        controller.updateComponent(placement.code, useGrip.code);
                    }
                }
            }
        },

        /**
         * Select default shafts.
         */
        pickDefaultShafts(controller) {
            // eslint-disable-next-line no-restricted-syntax
            for (const group of controller.blueprint.groups) {
                // eslint-disable-next-line no-restricted-syntax
                for (const placement of group.placements) {
                    if (placement.custom?.['t-default-shaft']) {
                        // Find a default shaft.
                        let useShaft = placement.components.find((x) => !x.placeholder && controller.isAvailable(x)
                            && x.custom?.['v-parent-id'] === placement.custom?.['t-default-shaft']
                            && x.custom?.['v-default-shaft'] === 'true');

                        if (useShaft == null) {
                            useShaft = placement.components.find((x) => !x.placeholder && controller.isAvailable(x)
                                && x.custom?.['v-parent-id'] === placement.custom?.['t-default-shaft']
                                && x.custom?.['v-length-default'] === 'true');
                        }

                        if (useShaft == null) {
                            useShaft = placement.components.find((x) => !x.placeholder && controller.isAvailable(x)
                                && x.custom?.['v-parent-id'] === placement.custom?.['t-default-shaft']);
                        }

                        if (useShaft == null) {
                            useShaft = placement.components.find((x) => !x.placeholder && controller.isAvailable(x));
                        }

                        if (useShaft) {
                            controller.updateComponent(placement.code, useShaft.code);
                        }
                    }
                }
            }
        },

        /**
         * Select default headcovers.
         */
        pickDefaultHeadcovers(controller) {
            const region = this.options.region || 'US';
            const applicableValue = controller.blueprint.custom[`v-headcovers-${region}`] || '';
            const applicableList = applicableValue.split(',').map((x) => x.trim()).filter((x) => x.length > 0);
            const group = controller.blueprint.groups.find((x) => x.code === 'Headcover' || x.name === 'Headcover');

            if (!group || applicableList.length === 0) {
                return;
            }

            // eslint-disable-next-line no-restricted-syntax
            for (const placement of group.placements) {
                const selected = controller.selectedAtPlacement(placement.code);

                if (selected && !selected.placeholder) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const stockOptions = placement.components
                    .filter((component) => !component.placeholder
                        && controller.isAvailable(component)
                        && component.custom?.['v-custom-shop'] !== 'true'
                        && applicableList.includes(component.custom?.['v-id']))
                    .sort((a, b) => +(a.custom?.['v-rank'] || '1') - +(b.custom?.['v-rank'] || '1'));

                const defaultHeadcover = stockOptions[0];

                if (defaultHeadcover) {
                    controller.updateComponent(placement.code, defaultHeadcover.code);
                }
            }
        },

        /**
         * Select default shaft bands.
         */
        pickDefaultShaftBands(controller) {
            const group = controller.blueprint.groups.find((x) => x.code === 'PutterShaftBand' || x.name === 'Shaft Band');

            if (!group) {
                return;
            }

            // eslint-disable-next-line no-restricted-syntax
            for (const placement of group.placements) {
                const selected = controller.selectedAtPlacement(placement.code);

                if (selected && !selected.placeholder) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const stockOptions = placement.components
                    .filter((component) => !component.placeholder
                        && controller.isAvailable(component)
                        && component.custom?.['v-custom-shop'] !== 'true')
                    .sort((a, b) => +(a.custom?.['v-rank'] || '1') - +(b.custom?.['v-rank'] || '1'));

                const defaultShaftBand = stockOptions[0];

                if (defaultShaftBand) {
                    controller.updateComponent(placement.code, defaultShaftBand.code);
                    this.addGroupToSeen(group.name);
                }
            }
        },

        /**
         * Select default grips, shafts, headcovers, and shaft bands.
         */
        pickDefaultShaftsGripsHeadcoversShaftBands(controller) {
            this.pickDefaultGrips(controller);
            this.pickDefaultShafts(controller);
            this.pickDefaultHeadcovers(controller);
            this.pickDefaultShaftBands(controller);
        },

        pickDefaultFlightLines(controller) {
            for (const group of controller.blueprint.groups) {
                // Apparently Titleist is using the older version of code... at some point something used group.name and that error
                // has been propagating through configurator variations...
                if (group.name === 'Flight Lines') {
                    for (const placement of group.placements) {
                        // controller.updateComponent(placement.code, placement.components[0].code);
                        controller.updateComponent(placement.code, 'FlightLine-FlightLine-NoThanks');
                        this.addGroupToSeen(group.name);
                    }
                }
            }
        },

        selectBlankFlightLines() {
            this.clearGenericPlacements('Flight Lines');
        },

        /**
         * Sets initial selections.
         */
        setInitialSelections(controller) {
            // Set initial-initial data, based on metadata configuration.
            this.pickDefaultColors(controller);

            // Select default grips, shafts, headcovers, and shaft bands.
            this.pickDefaultShaftsGripsHeadcoversShaftBands(controller);

            this.pickDefaultFlightLines(controller);

            this.hasChanged = false;

            if (this.initialData && this.initialRecipe) {
                // Set up features from the configuration data.

                this.hasChanged = true;

                // eslint-disable-next-line no-restricted-syntax
                for (const s of this.initialRecipe.components) {
                    if (s.code === 'Wedge') {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const placement = controller.findPlacement(s.code);

                    const selected = placement.components.find((c) => controller.isAvailable(c)
                        && c.placementIndependentCode === s.component.placementIndependentCode);

                    if (selected != null) {
                        controller.updateComponent(s.code, selected.code);

                        if (s.component.custom['v-stamping-use-symbols-addon'] === 'yes') {
                            controller.commitCustomAttributes(s.code, {
                                'v-stamping-use-symbols-addon': 'yes',
                            });
                        }

                        if (s.component.custom['v-stamping-text-1']) {
                            controller.commitCustomAttributes(s.code, {
                                'v-stamping-text-1': s.component.custom['v-stamping-text-1'],
                            });
                        }

                        if (s.component.custom['v-stamping-text-2']) {
                            controller.commitCustomAttributes(s.code, {
                                'v-stamping-text-2': s.component.custom['v-stamping-text-2'],
                            });
                        }

                        // Find the respective group.
                        // eslint-disable-next-line no-restricted-syntax
                        for (const g of controller.blueprint.groups) {
                            if (g.placements.some((x) => x.code === s.code)) {
                                if (g.name === 'Character Stamping' || g.name === 'Stamps' || g.name === 'Paintfill') {
                                    this.addGroupToSeen(g.name);
                                    this.addGroupToSeen('Face/Sole');
                                } else {
                                    this.addGroupToSeen(g.name);
                                }
                            }
                        }
                    }
                }

                // Restore palette.
                const paletteJson = this.initialRecipe.custom['v-palette'];
                if (paletteJson) {
                    const palette = JSON.parse(paletteJson);

                    this.paletteSwatchSlots = palette;

                    for (const hex of palette) {
                        const match = this.masterPalette.find((x) => x.value === hex);
                        if (match) {
                            match.selected = true;
                        }
                    }

                    const stepOptionsJson = this.initialRecipe.custom['v-palette-selections'];
                    if (stepOptionsJson) {
                        const stepOptions = JSON.parse(stepOptionsJson);
                        const paintfillSelectionOrderJson = this.initialRecipe.custom['v-paintfill-selection-order'];

                        // TODO: Ideally we check compatibility.
                        this.uiStepSubOptions = stepOptions;

                        if (paintfillSelectionOrderJson) {
                            this.uiPaintfillSelectionOrder = JSON.parse(paintfillSelectionOrderJson)
                                .filter((groupName) => PAINTFILL_GROUPS[groupName]);
                            this.prunePaintfillSelectionOrder();
                        } else {
                            this.rebuildPaintfillSelectionOrder();
                        }
                    }
                }

                // Wipe out initial data since it was already used.
                this.initialData = null;
                this.initialRecipe = null;
            } else {
                if (this.options.initialShaft && this.options.initialShaft.length > 0) {
                    const shafts = controller.findPlacement('Shaft');

                    let useShaft = null;

                    // eslint-disable-next-line no-restricted-syntax
                    for (const s of (shafts?.components ?? [])) {
                        if (!controller.isAvailable(s)) {
                            // eslint-disable-next-line no-continue
                            continue;
                        }

                        let shaftId = null;

                        for (let i = 0; i < 20; i += 1) {
                            const key = `v-order-${i}-item`;

                            if (s.custom[key] === 'shaft-id') {
                                shaftId = s.custom[`v-order-${i}-code`];
                            }
                        }

                        if (shaftId === this.options.initialShaft) {
                            useShaft = s;
                            break;
                        }
                    }

                    if (useShaft) {
                        controller.updateComponent(shafts.code, useShaft.code);
                    }
                }

                if (this.options.initialGrip && this.options.initialGrip.length > 0) {
                    const grips = controller.findPlacement('Grip');

                    let useGrip = null;

                    // eslint-disable-next-line no-restricted-syntax
                    for (const g of (grips?.components ?? [])) {
                        if (!controller.isAvailable(g)) {
                            // eslint-disable-next-line no-continue
                            continue;
                        }

                        let gripId = null;

                        for (let i = 0; i < 20; i += 1) {
                            const key = `v-order-${i}-item`;

                            if (g.custom[key] === 'grip-id') {
                                gripId = g.custom[`v-order-${i}-code`];
                            }
                        }

                        if (gripId === this.options.initialGrip) {
                            useGrip = g;
                            break;
                        }
                    }

                    if (useGrip) {
                        controller.updateComponent(grips.code, useGrip.code);
                    }
                }

                if (this.options.initialAdjustLoft && this.options.initialAdjustLoft.length > 0) {
                    const adjustments = controller.findPlacement('LoftAdjustment');

                    let useOption = null;

                    let adjustmentCode = this.options.initialAdjustLoft;
                    if (adjustmentCode.includes(':')) {
                        adjustmentCode = adjustmentCode.split(':')[1];
                    }

                    // eslint-disable-next-line no-restricted-syntax
                    for (const o of (adjustments?.components ?? [])) {
                        if (o.custom['v-ags-code'] === adjustmentCode) {
                            useOption = o;
                        }
                    }

                    if (useOption) {
                        controller.updateComponent(adjustments.code, useOption.code);
                    }
                }

                if (this.options.initialAdjustWeight && this.options.initialAdjustWeight.length > 0) {
                    const adjustments = controller.findPlacement('WeightAdjustment');

                    let useOption = null;

                    let adjustmentCode = this.options.initialAdjustWeight;
                    if (adjustmentCode.includes(':')) {
                        adjustmentCode = adjustmentCode.split(':')[1];
                    }

                    // eslint-disable-next-line no-restricted-syntax
                    for (const o of (adjustments?.components ?? [])) {
                        if (o.custom['v-ags-code'] === adjustmentCode) {
                            useOption = o;
                        }
                    }

                    if (useOption) {
                        controller.updateComponent(adjustments.code, useOption.code);
                    }
                }

                if (this.options.initialAdjustLean && this.options.initialAdjustLean.length > 0) {
                    const adjustments = controller.findPlacement('LeanAdjustment');

                    let useOption = null;

                    let adjustmentCode = this.options.initialAdjustLean;
                    if (adjustmentCode.includes(':')) {
                        adjustmentCode = adjustmentCode.split(':')[1];
                    }

                    // eslint-disable-next-line no-restricted-syntax
                    for (const o of (adjustments?.components ?? [])) {
                        if (o.custom['v-ags-code'] === adjustmentCode) {
                            useOption = o;
                        }
                    }

                    if (useOption) {
                        controller.updateComponent(adjustments.code, useOption.code);
                    }
                }

                if (this.options.initialAdjustLie && this.options.initialAdjustLie.length > 0) {
                    const adjustments = controller.findPlacement('LieAdjustment');

                    let useOption = null;

                    let adjustmentCode = this.options.initialAdjustLie;
                    if (adjustmentCode.includes(':')) {
                        adjustmentCode = adjustmentCode.split(':')[1];
                    }

                    // eslint-disable-next-line no-restricted-syntax
                    for (const o of (adjustments?.components ?? [])) {
                        if (o.custom['v-ags-code'] === adjustmentCode) {
                            useOption = o;
                        }
                    }

                    if (useOption) {
                        controller.updateComponent(adjustments.code, useOption.code);
                    }
                }
            }
        },

        /**
         * Check for a dependent AGS code mapping.
         */
        checkAgsDependent(controller, agsCode, agsOption) {
            // eslint-disable-next-line no-restricted-syntax
            for (const s of controller.state) {
                // Select by code.
                // eslint-disable-next-line no-restricted-syntax
                for (const c of s.placement.components) {
                    for (let i = 1; i <= 9; i += 1) {
                        this.checkAgsDependentFilledText(controller, s.placement, c, `${i}`, agsCode, agsOption);
                    }
                }

                if (s.component.custom['v-stamping-use-symbols-addon'] === 'yes') {
                    for (let i = 1; i <= 9; i += 1) {
                        this.checkAgsDependentText(controller, s.placement, s.component, `${i}-snow`, agsCode, agsOption);
                        this.checkAgsDependentFilledText(controller, s.placement, s.component, `${i}-snow`, agsCode, agsOption);
                        this.checkAgsDependentComponent(controller, s.component, `${i}-snow`, agsCode, agsOption);
                    }
                }

                for (let i = 1; i <= 9; i += 1) {
                    this.checkAgsDependentText(controller, s.placement, s.component, `${i}`, agsCode, agsOption);
                    this.checkAgsDependentFilledText(controller, s.placement, s.component, `${i}`, agsCode, agsOption);
                    this.checkAgsDependentComponent(controller, s.component, `${i}`, agsCode, agsOption);
                }

                // Select by code.
                // eslint-disable-next-line no-restricted-syntax
                for (const c of s.placement.components) {
                    for (let i = 1; i <= 9; i += 1) {
                        this.checkAgsDependentThisComponent(controller, s.placement, c, `${i}`, agsCode, agsOption);
                    }
                }
            }
        },

        /**
         * Restore a text value.
         */
        checkAgsDependentText(controller, placement, component, i, code, value) {
            if (component.custom[`v-ags-also-${i}-feature`] === code) {
                if (component.custom[`v-ags-also-${i}-text`]) {
                    if (value != null && value.length > 0) {
                        // eslint-disable-next-line
                        console.log(`... set text ${component.custom[`v-ags-also-${i}-text`]} to ${value}`);

                        controller.commitCustomAttributes(placement.code, {
                            [component.custom[`v-ags-also-${i}-text`]]: value,
                        });
                    }
                }
            }
        },

        /**
         * Restore a text value.
         */
        checkAgsDependentFilledText(controller, placement, component, i, code, value) {
            if (component.custom[`v-ags-also-${i}-feature`] === code) {
                if (component.custom[`v-ags-also-${i}-filled-text`]) {
                    const [agsCode] = component.custom[`v-ags-also-${i}-filled-text`].split(',').map((x) => x.trim()).filter((x) => x.length > 0);

                    if (agsCode === value) {
                        controller.updateComponent(placement.code, component.code);
                    }
                }
            }
        },

        /**
         * Select a value in another placement.
         */
        checkAgsDependentComponent(controller, component, i, code, value) {
            if (component.custom[`v-ags-also-${i}-feature`] === code) {
                if (component.custom[`v-ags-also-${i}-from`]) {
                    if (value != null && value.length > 0) {
                        const otherPlacement = controller.findPlacement(component.custom[`v-ags-also-${i}-from`]);
                        if (otherPlacement) {
                            const select = otherPlacement.components.find((x) => x.custom['v-ags-code'] === value);

                            if (select) {
                                controller.updateComponent(otherPlacement.code, select.code);
                            }
                        }
                    }
                }
            }
        },

        /**
         * Select a value in another placement.
         */
        checkAgsDependentThisComponent(controller, placement, component, i, code, value) {
            let currentCode = null;

            const current = controller.selectedAtPlacement(placement.code);
            if (current && current.custom?.['v-initial-selection'] === 'yes') {
                currentCode = current.custom['v-ags-code'];
            }

            // Only look at components with the same ags code.
            if (currentCode) {
                if (component.custom['v-ags-code'] === currentCode
                    && component.custom[`v-ags-also-${i}-feature`] === code
                    && component.custom[`v-ags-also-${i}-code`] === value) {
                    // Untag current selection.
                    controller.commitCustomAttributes(placement.code, {
                        'v-initial-selection': 'no',
                    });

                    // Select this component.
                    controller.updateComponent(placement.code, component.code);

                    // Tag the selection again.
                    controller.commitCustomAttributes(placement.code, {
                        'v-initial-selection': 'yes',
                    });

                    // eslint-disable-next-line
                    console.log(`... found (1) on ${placement.code} -> ${component.code}`);
                }
            } else if (component.custom[`v-ags-also-${i}-feature`] === code && component.custom[`v-ags-also-${i}-code`] === value) {
                // Select this component.
                controller.updateComponent(placement.code, component.code);
                // eslint-disable-next-line
                console.log(`... found (2) on ${placement.code} -> ${component.code}`);
            }

            if (component.custom[`v-ags-also-${i}-feature-once`] === code) {
                if (component.custom[`v-ags-also-${i}-code`] === value) {
                    // Select this component.
                    controller.updateComponent(placement.code, component.code);
                    // eslint-disable-next-line
                    console.log(`... found (3) on ${placement.code} -> ${component.code}`);
                }
            }
        },

        /**
         * Check for a direct AGS code mapping: component -> code.
         */
        checkAgsPlacementOption(controller, agsCode, agsOption) {
            // eslint-disable-next-line no-restricted-syntax
            for (const s of controller.state) {
                if (s.placement.custom?.['v-ags-feature'] === agsCode) {
                    let select = null;
                    let enableAddon = false;

                    // eslint-disable-next-line no-restricted-syntax
                    for (const c of s.placement.components) {
                        if (c.custom['v-ags-code'] === agsOption) {
                            select = c;
                            break;
                        }

                        // Special case for addons redirect.
                        if (c.custom['v-stamping-supports-snow-symbol'] === agsOption) {
                            select = c;
                            enableAddon = true;
                            break;
                        }
                    }

                    if (select) {
                        // eslint-disable-next-line
                        console.log(`... found on ${s.placement.code} -> ${select.code}`);

                        controller.updateComponent(s.placement.code, select.code);

                        controller.commitCustomAttributes(s.placement.code, {
                            'v-initial-selection': 'yes',
                        });

                        if (enableAddon) {
                            controller.commitCustomAttributes(s.placement.code, {
                                'v-stamping-use-symbols-addon': 'yes',
                            });
                        }
                    } else {
                        // eslint-disable-next-line
                        console.log('... but not found');
                    }

                    return true;
                }
            }

            return false;
        },

        /**
         * Loads defaults assemblies for the current controller.
         */
        loadDefaultAssemblies() {
            const sockets = this.viewController.allSockets();

            if (sockets && sockets.length > 0) {
                const someAttached = sockets.some((s) => s.to);

                if (!someAttached) {
                    const prebuilt = this.prebuiltOptions?.find((o) => o.isDefault)
                        ?? this.prebuiltOptions?.[0];

                    if (prebuilt) {
                        // Reload the current controller with new default choices.
                        this.reloadController(prebuilt.product).then((controller) => {
                            // Set initial-initial data, based on metadata configuration.
                            this.pickDefaultColors(controller);

                            // Select default grips, shafts, headcovers, and shaft bands.
                            this.pickDefaultShaftsGripsHeadcoversShaftBands(controller);

                            this.showController(controller);
                        });
                    }
                }
            }
        },

        /**
         * Transfer state from the old controller to the new one, if compatible.
         */
        transferState(to, from) {
            const ignorePlacements = ['Wedge'];

            // eslint-disable-next-line no-restricted-syntax
            for (const s of from.state) {
                if (ignorePlacements.includes(s.placement.code)) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const newPlacement = to.findPlacement(s.placement.code);
                if (newPlacement == null) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const newSelection = newPlacement.components.find((c) => c.placementIndependentCode === s.component.placementIndependentCode
                    && to.isAvailable(c));

                if (newSelection == null) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                to.updateComponent(newPlacement.code, newSelection.code);

                if (s.component.custom['v-stamping-use-symbols-addon'] === 'yes') {
                    to.commitCustomAttributes(newPlacement.code, {
                        'v-stamping-use-symbols-addon': 'yes',
                    });
                }

                if (s.component.custom['v-stamping-text-1']) {
                    to.commitCustomAttributes(newPlacement.code, {
                        'v-stamping-text-1': s.component.custom['v-stamping-text-1'],
                    });
                }

                if (s.component.custom['v-stamping-text-2']) {
                    to.commitCustomAttributes(newPlacement.code, {
                        'v-stamping-text-2': s.component.custom['v-stamping-text-2'],
                    });
                }
            }
        },

        /**
         * Reloads controller with a different set of products.
         */
        reloadController(product) {
            return new Promise((resolve) => {
                const reloaded = new this.options.DriveCustomizer.Controller({
                    ...this.defaultControllerOptions(),
                    ...{
                        products: [product],
                    },
                });

                // reloaded.traceAllowed = true;
                // reloaded.traceAvailable = true;
                // reloaded.traceUpdated = true;

                reloaded.on('afterInitializeState', () => {
                    // Set initial choices.
                    this.setInitialSelections(reloaded);

                    // Copy data from the old controller.
                    this.transferState(reloaded, this.viewController);
                });

                reloaded.on('afterInitializeBlueprint', () => {
                    resolve(reloaded);
                });

                this.loadController(reloaded);
            });
        },

        /**
         * Selects a controller to show in the UI.
         */
        showController(controller) {
            this.viewController = controller;

            this.undoStack = [];

            // Reset transient Front UI flags when switching controllers/products
            this.uiFrontActiveSections = {};
            this.uiFrontNestedPanelValidationAttempted = {};
            this.resetStepSubOptionsState();

            this.stateChanged();
        },

        /**
         * Reloads controllers with the new bundle while attempting to save the current printable configuration.
         */
        selectBundle(product) {
            this.reloadController(product).then((controller) => {
                this.showController(controller);
            });
        },

        /**
         * Puts the app in an error state if the user tries to add to cart before all selections have been made.
         */
        setErrorState(state) {
            this.inErrorState = state;
        },

        setPanelsInErrorState(state) {
            this.panelsInErrorState = state;
        },

        removePanelFromErrorState(panel) {
            this.panelsInErrorState = this.panelsInErrorState
                .filter((p) => p !== panel);
        },

        addPanelToErrorState(panel) {
            if (this.panelsInErrorState.includes(panel)) {
                return;
            }
            this.panelsInErrorState.push(panel);
        },

        /**
         * Save undo checkpoint.
         */
        undoCheckpoint() {
            // There is no undo button so we don't need a checkpoint.

            // Push the current state into the undo stack so it could be restored later.
            // this.undoStack.push(this.viewController.clone());
        },

        resetGroupLocks() {
            this.groupLocks = buildInitialGroupLocks();
        },

        isCustomShopSelectedAtPlacement(placementCode) {
            const selected = this.viewController?.selectedAtPlacement(placementCode);

            return selected?.custom?.['v-custom-shop'] === 'true';
        },

        resetLockedGroupsToDefaults() {
            if (this.isCustomShopSelectedAtPlacement('Grip')) {
                this.resetGrip();
            }

            if (this.isCustomShopSelectedAtPlacement('Headcover')) {
                this.resetHeadcover();
            }

            if (this.isCustomShopSelectedAtPlacement('PutterShaftBand')) {
                this.resetShaftBand();
            }
        },

        syncGroupLocksFromSelections() {
            if (!this.viewController) {
                return;
            }

            const hasUnlockingCustomization = GROUP_LOCK_UNLOCK_PLACEMENTS
                .some((placementCode) => {
                    const selected = this.viewController.selectedAtPlacement(placementCode);

                    return selected && !selected.placeholder;
                })
                || this.isPaintfillComplete('Face/Sole');

            const nextLocked = !hasUnlockingCustomization;
            const shouldResetLockedGroups = nextLocked
                && Array.from(LOCKED_GROUPS).some((groupName) => this.groupLocks?.[groupName] === false);
            const nextGroupLocks = {
                ...(this.groupLocks || {}),
            };
            let hasChanged = false;

            LOCKED_GROUPS.forEach((groupName) => {
                if (nextGroupLocks[groupName] !== nextLocked) {
                    nextGroupLocks[groupName] = nextLocked;
                    hasChanged = true;
                }
            });

            if (hasChanged) {
                this.groupLocks = nextGroupLocks;
            }

            if (shouldResetLockedGroups) {
                this.resetLockedGroupsToDefaults();
            }
        },

        setGroupLocked(groupOrName, locked = true) {
            const normalizedGroupName = normalizeGroupNameForLock(groupOrName);

            if (!normalizedGroupName || !LOCKED_GROUPS.has(normalizedGroupName)) {
                return;
            }

            this.groupLocks = {
                ...this.groupLocks,
                [normalizedGroupName]: !!locked,
            };
        },

        lockGroup(groupOrName) {
            this.setGroupLocked(groupOrName, true);
        },

        unlockGroup(groupOrName) {
            this.setGroupLocked(groupOrName, false);
        },

        isGroupLocked(groupOrName) {
            const normalizedGroupName = normalizeGroupNameForLock(groupOrName);

            return !!this.groupLocks?.[normalizedGroupName];
        },

        /**
         * Populates the shared palette with currently applied initials and stamp colors when paintfill is enabled.
         * Excludes raw color.
         */
        seedPaletteFromAppliedInitialsAndStampColors() {
            const selectedValues = new Set();
            const options = [
                ...(this.uiStepSubOptions?.frontInitials || []),
                ...(this.uiStepSubOptions?.frontStamps || []),
            ];

            for (const option of options) {
                const value = this.getAppliedPaletteValueForStepSubOption(option);

                if (value) {
                    const paletteOption = paletteOptionFromValue(this.masterPalette, value);

                    if (!paletteOption?.raw) {
                        selectedValues.add(value);
                    }
                }
            }

            this.setPaletteSwatchSlots([...selectedValues]);
            this.syncMasterPaletteSelectedValues(selectedValues);
        },

        /**
         * Stores the previous selection to be used for Undo functionality.
         * Updates the selected component.
         */
        setSelection(placementCode, optionValue) {
            this.undoCheckpoint();
            const hadPaintfill = this.isAnyPaintfillSelected();

            // Update the current controller.
            this.viewController.updateComponent(placementCode, optionValue);

            // If no paintfill was selected before, and now it is, update the palette.
            if (!hadPaintfill && this.isAnyPaintfillSelected()) {
                this.seedPaletteFromAppliedInitialsAndStampColors();
            }
        },

        /**
         * Sets the current current group.
         */
        setCurrentGroup(groupName) {
            const appStore = useAppStore();

            this.currentGroup = groupName;

            if (!appStore.isMobile) {
                this.addGroupToSeen(groupName);
            }
        },

        /**
         * Sets the current current group.
         */
        addGroupToSeen(groupName) {
            setTimeout(() => {
                if (!this.groupsSeen.includes(groupName)) {
                    this.groupsSeen.push(groupName);
                }
            }, 500); // delay for UI update
        },

        /**
         * Sets the current current group to the next group.
         */
        enableNextGroup() {
            const currentGroupIndex = this.visibleGroups.findIndex((group) => group.name === this.currentGroup);
            const nextGroup = this.visibleGroups[currentGroupIndex + 1];

            if (nextGroup) {
                this.setCurrentGroup(nextGroup.name);
            }
        },

        /**
         * Sets the current current group to the previous group.
         */
        enablePreviousGroup() {
            const currentGroupIndex = this.visibleGroups.findIndex((group) => group.name === this.currentGroup);
            const previousGroup = this.visibleGroups[currentGroupIndex - 1];

            if (previousGroup) {
                this.setCurrentGroup(previousGroup.name);
            }
        },

        /**
         * Return to the last made selection.
         */
        undoSelections() {
            if (this.undoStack.length > 0) {
                // Only move back to the previous step if no selection has been made in the current group
                const isFirstGroup = this.currentGroup === this.visibleGroups?.[0].name;
                const selectionMadeForCurrentGroup = this.selectionsMade.includes(this.currentGroup);

                if (!isFirstGroup && !selectionMadeForCurrentGroup) {
                    this.enablePreviousGroup();
                }

                // Take the last captured controller and make it active.
                const lastState = this.undoStack.pop();

                this.showController(lastState);
            }
        },

        /**
         * Helper method, get a list of visible placements.
         */
        visiblePlacements(group) {
            if (group.placements == null) {
                return null;
            }

            return group.placements.filter((placement) => {
                if (placement?.custom?.['v-hidden'] === 'true') {
                    return false;
                }

                return true;
            });
        },

        hasPlacementOptions(groupName, placementCode = groupName) {
            if (!groupName || !placementCode) {
                return false;
            }

            const group = this.visibleGroups.find((item) => (
                item.name === groupName
                || item.code === groupName
            ));
            const placement = group?.placements?.find((item) => (
                item.code === placementCode
                || item.name === placementCode
            ));

            return placement?.components?.some((component) => (
                !component.placeholder
                && component.available !== false
                && (!this.viewController?.isAvailable || this.viewController.isAvailable(component))
            )) || false;
        },

        /**
         * Save customizer recipe.
         */
        saveRecipe(controller, options) {
            const useController = controller ?? this.viewController;

            return new Promise((resolve) => {
                useController.saveRecipe({
                    temporaryRecipe: options && options.temporary,

                    translateRecipeAfter: (recipe) => {
                        recipe.custom['v-preview-url'] = this.getPreviewImageUrl(useController, 'Face');

                        recipe.custom['v-palette'] = JSON.stringify(this.paletteSwatchSlots);
                        recipe.custom['v-palette-selections'] = JSON.stringify(this.uiStepSubOptions);
                        recipe.custom['v-paintfill-selection-order'] = JSON.stringify(this.paintfillSelectionOrder);

                        const products = [];

                        // eslint-disable-next-line no-restricted-syntax
                        for (const p of this.selectedProducts) {
                            products.push(p.custom?.['t-setcomp']);
                        }

                        for (const v of recipe.views) {
                            const matchView = useController.blueprint.views.find((x) => x.code === v.code);

                            if (matchView?.bakeInRecipe) {
                                v.preview = this.getPreviewImageUrl(controller, v.code);
                            }
                        }

                        recipe.custom['t-selected-setcomp'] = JSON.stringify(products);
                    },
                }).then((recipe) => {
                    // Fetch the actual recipe.
                    fetch(recipe.location).then((response) => response.json()).then((data) => {
                        resolve(data);
                    });
                });
            });
        },

        /**
         * Save controller before sharing.
         */
        beforeShare(controller) {
            const useController = controller ?? this.viewController;

            return new Promise((resolve) => {
                const appStore = useAppStore();

                appStore.setIsBusy(true);

                this.saveRecipe(useController, {
                    temporary: true,
                }).then((recipe) => {
                    appStore.setIsBusy(false);

                    resolve({
                        recipe,
                    });
                });
            });
        },

        /**
         * Add to cart, notify the host, and display modal.
         */
        addCurrentSelectionsToCart() {
            if (this.currentCartItem) {
                return this.addToCart(
                    this.currentCartItem.controller,
                    this.currentCartItem.lineItemId,
                );
            }

            if (this.options.lineItemId) {
                return this.addToCart(this.viewController, this.options.lineItemId);
            }

            return this.addToCart(this.viewController);
        },

        /**
         * Add to cart, notify the host, and display modal.
         */
        addToCart(controller, lineItemId) {
            const useController = controller ?? this.viewController;

            return new Promise((resolve, reject) => {
                const appStore = useAppStore();

                appStore.setIsBusy(true);

                this.saveRecipe(useController).then((recipe) => {
                    this.sendToCart(useController, lineItemId, recipe).then((payload) => {
                        // appStore.setIsBusy(false);
                        payload[0].payload.recipe = recipe.id;

                        if (this.options.onAddToCart) {
                            if (this.options.onAddToCart(payload, recipe, lineItemId)) {
                                appStore.globalDone();
                            }
                        }

                        resolve({
                            recipe,
                        });
                    }).catch(() => {
                        appStore.setIsBusy(false);

                        reject();
                    });
                });
            });
        },

        /**
         * Add to cart, notify the host, and display modal.
         */
        addToWishlist(controller, lineItemId) {
            const useController = controller ?? this.viewController;

            return new Promise((resolve, reject) => {
                const appStore = useAppStore();

                appStore.setIsBusy(true);

                this.saveRecipe(useController).then((recipe) => {
                    this.sendToCart(useController, lineItemId, recipe).then((payload) => {
                        // appStore.setIsBusy(false);
                        payload[0].payload.recipe = recipe.id;

                        if (this.options.onAddToWishlist) {
                            if (this.options.onAddToWishlist(payload, recipe, lineItemId)) {
                                appStore.globalDone();
                            }
                        }

                        resolve({
                            recipe,
                        });
                    }).catch(() => {
                        appStore.setIsBusy(false);

                        reject();
                    });
                });
            });
        },

        /**
         * Add to cart, notify the host, and display modal.
         */
        saveAndExit(controller, lineItemId) {
            const useController = controller ?? this.viewController;

            return new Promise((resolve, reject) => {
                const appStore = useAppStore();

                appStore.setIsBusy(true);

                this.saveRecipe(useController).then((recipe) => {
                    this.sendToCart(useController, lineItemId, recipe).then((payload) => {
                        // appStore.setIsBusy(false);
                        payload[0].payload.recipe = recipe.id;

                        if (this.options.onSave) {
                            if (this.options.onSave(payload, recipe, lineItemId)) {
                                appStore.globalDone();
                            }
                        }

                        resolve({
                            recipe,
                        });
                    }).catch(() => {
                        appStore.setIsBusy(false);

                        reject();
                    });
                });
            });
        },

        /**
         * Add controller to our virtual "cart".
         */
        addInstanceToCart(controller, recipe, lineItemId) {
            this.cart.push({
                controller,
                recipe,
                lineItemId,
            });
        },

        /**
         * Puts a controller into the edit mode.
         */
        editInstance(item) {
            this.currentCartItem = item;

            this.showController(item.controller);
            this.restoreNoCustomizationSelections(item.controller, item.recipe);

            this.setCurrentGroup(this.visibleGroups?.[0].name);
        },

        updateInstanceRecipe(recipe, lineItemId) {
            if (this.currentCartItem) {
                this.currentCartItem.recipe = recipe;
                this.currentCartItem.lineItemId = lineItemId;
            }
        },

        setClubSetController(controller) {
            this.clubSetController = controller;
        },

        /**
         * The widget has done whatever it was doing and now it is time to close. Should we destroy the instance or just keep it
         * (for example if the host page redirects anyway).
         */
        globalDone() {
            const appStore = useAppStore();

            appStore.globalDone();
        },

        resetStampSymbols() {
            const symbols = this.viewController.findPlacement('StampSymbols');

            if (symbols) {
                const symbolNone = symbols.components.find((x) => x.placeholder);

                if (symbolNone) {
                    this.viewController.updateComponent(symbols.code, symbolNone.code);
                }
            }
        },

        /**
         * Selects a stamping style and adjusts options.
         */
        selectStampingStyle(placementCode, componentCode) {
            this.undoCheckpoint();

            if (this.viewController.updateComponent(placementCode, componentCode)) {
                const selected = this.viewController.selectedAtPlacement(placementCode);

                // Turn off addons.
                this.viewController.commitCustomAttributes(placementCode, {
                    'v-stamping-use-symbols-addon': '',
                });

                if (selected.custom?.['v-stamping-has-text-1'] !== 'yes') {
                    this.viewController.commitCustomAttributes(placementCode, {
                        'v-stamping-text-1': '',
                    });
                }

                if (selected.custom?.['v-stamping-has-text-2'] !== 'yes') {
                    this.viewController.commitCustomAttributes(placementCode, {
                        'v-stamping-text-2': '',
                    });
                }

                if (selected.custom?.['v-stamping-has-symbols'] !== 'yes') {
                    this.resetStampSymbols();
                }
            }
        },

        async resetSpecifications() {
            this.removePanelFromErrorState('Specifications');

            // Find the default product based on the initial orientation.
            const defaultOrientation = this.options.initialOrientation
                || this.viewController.blueprint.custom['t-default-orientation'];
            // Find matching product and sort by Length option.
            const defaultProduct = this.allProducts
                .filter((product) => product.custom?.['t-orientation'] === defaultOrientation
                    && product.custom?.['t-setcomp'] // Length option
                    && product.custom?.['t-setcomp-name']) // Length option name
                // Sort to get first item (shortest length)
                .sort((a, b) => {
                    const aSetcomp = a.custom?.['t-setcomp'] ?? '';
                    const bSetcomp = b.custom?.['t-setcomp'] ?? '';

                    if (aSetcomp < bSetcomp) {
                        return -1;
                    }

                    if (aSetcomp > bSetcomp) {
                        return 1;
                    }

                    return 0;
                })[0];

            // Select the default product.
            this.selectedProducts = defaultProduct ? [defaultProduct] : [];
            await this.loadSelectedProducts(); // Update the controller to the selected product.

            const loftPlaceholder = getPlaceholderForPlacement(this.viewController.findPlacement('LoftAdjustment'), this.viewController);
            if (loftPlaceholder) {
                this.viewController.updateComponent('LoftAdjustment', loftPlaceholder.code);
            }

            const leanPlaceholder = getPlaceholderForPlacement(this.viewController.findPlacement('LeanAdjustment'), this.viewController);
            if (leanPlaceholder) {
                this.viewController.updateComponent('LeanAdjustment', leanPlaceholder.code);
            }

            const liePlaceholder = getPlaceholderForPlacement(this.viewController.findPlacement('LieAdjustment'), this.viewController);
            if (liePlaceholder) {
                this.viewController.updateComponent('LieAdjustment', liePlaceholder.code);
            }

            // // We have cleared products, but we now need to reload the controller to the default finish and orientation... and some loft.
            // const defaultOrientation = this.viewController.blueprint.custom['t-default-orientation'];
            // const defaultFinish = this.viewController.blueprint.custom['t-default-finish'];

            // // Find a product that matches finish and orientation, ignoring AGS code - those are finish specific anyway.
            // const defaultProduct = this.allProducts.find((x) => {
            //     if (x.custom['t-orientation'] === defaultOrientation && x.custom['t-finish'] === defaultFinish) {
            //         return true;
            //     }

            //     return false;
            // });

            // if (defaultProduct) {
            //     // Reload controller.
            //     this.reloadController(defaultProduct.styleCode).then((controller) => {
            //         // Set initial-initial data, based on metadata configuration.
            //         this.pickDefaultColors(controller);

            //         // Select default grips, shafts, headcovers, and shaft bands.
            //         this.pickDefaultShaftsGripsHeadcoversShaftBands(controller);

            //         this.showController(controller);
            //     });
            // }
        },

        resetShaft() {
            const shaftPlacement = this.visibleGroups.find((placement) => placement.code === 'Shaft');

            if (!shaftPlacement) {
                return;
            }

            this.pickDefaultShafts(this.viewController);
        },

        resetGrip() {
            const gripPlacement = this.visibleGroups.find((placement) => placement.code === 'Grip');

            if (!gripPlacement) {
                return;
            }

            this.pickDefaultGrips(this.viewController);
        },

        resetHeadcover() {
            const placements = this.visibleGroups.find((group) => group.name === 'Headcover')?.placements;

            if (!placements) {
                return;
            }

            // Clear and restore synchronously so Vue does not render the blank selection.
            placements.forEach((placement) => {
                const placeholder = placement.components.find((component) => component.placeholder);

                if (placeholder) {
                    this.viewController.updateComponent(placement.code, placeholder.code);
                }
            });

            this.pickDefaultHeadcovers(this.viewController);
        },

        async clearGenericPlacements(groupName) {
            const placements = this.visibleGroups?.find((group) => group.name === groupName)?.placements;

            await new Promise((resolve) => { setTimeout(resolve, 100); }); // wait for input values to clear

            if (!placements) {
                return;
            }

            placements.forEach((placement) => {
                const placeholder = placement.components.find((x) => x.placeholder);
                if (placeholder) {
                    this.viewController.updateComponent(placement.code, placeholder.code);
                }
            });
        },

        async resetCharacterStamping() {
            const customAttributeCode = 'StampingStyle';
            const customFields = ['v-stamping-text-1', 'v-stamping-text-2'];

            this.removePanelFromErrorState('Character Stamping');
            this.clearFrontNestedPanelValidationAttempted('initials');
            if (this.isFrontSectionActive('initials')) {
                this.clearFrontActiveSection('initials');
            }
            this.clearStepSubOptionGroupColorSelections(['frontInitials']);

            const placements = this.visibleGroups?.find((group) => group.name === 'Character Stamping')?.placements;

            if (placements) {
                placements.forEach((placement) => {
                    if (placement.code === customAttributeCode) {
                        return;
                    }

                    const placeholder = getPlaceholderForPlacement(placement, this.viewController);

                    if (placeholder) {
                        this.viewController.updateComponent(placement.code, placeholder.code);
                    }
                });

                // eslint-disable-next-line no-restricted-syntax
                for (const field of customFields) {
                    this.viewController.commitCustomAttributes(customAttributeCode, {
                        [field]: '',
                    });
                }

                const mainPlacement = placements.find((placement) => placement.code === customAttributeCode);

                const blankPlacement = mainPlacement.components
                    .find((x) => x.placeholder && !x.placeholderOptional);

                this.viewController.updateComponent(mainPlacement.code, blankPlacement.code);
            }
        },

        syncPaintfillFrom(sourcePlacement) {
            const selectedAtSource = this.viewController.selectedAtPlacement(sourcePlacement);
            const isRequiredPlaceholder = selectedAtSource?.placeholder && !selectedAtSource?.placeholderOptional;
            const isOptionalPlaceholder = selectedAtSource?.placeholder && selectedAtSource?.placeholderOptional;

            if (isRequiredPlaceholder) {
                // Deselect other two.
                if (sourcePlacement !== 'PutterFrontPaintfill') {
                    this.resetPaintfill();
                }

                if (sourcePlacement !== 'PutterTopPaintfill') {
                    this.resetTop();
                }

                if (sourcePlacement !== 'PutterBackPaintfill') {
                    this.resetBack();
                }
            } else if (isOptionalPlaceholder) {
                // "No Customization" is an intentional selection for this panel only.
            } else {
                // Paintfill sections are independent. Selecting one should not activate the others.

                // Old method to enable other two.
                // if (sourcePlacement !== 'PutterFrontPaintfill') {
                //     const frontPlacement = this.viewController.findPlacement('PutterFrontPaintfill');
                //     if (frontPlacement) {
                //         const toSelect = frontPlacement.components.find((c) => !c.placeholder);
                //         if (toSelect) {
                //             this.viewController.updateComponent(frontPlacement.code, toSelect.code);
                //         }
                //     }
                // }

                // if (sourcePlacement !== 'PutterTopPaintfill') {
                //     const topPlacement = this.viewController.findPlacement('PutterTopPaintfill');
                //     if (topPlacement) {
                //         const toSelect = topPlacement.components.find((c) => !c.placeholder);
                //         if (toSelect) {
                //             this.viewController.updateComponent(topPlacement.code, toSelect.code);
                //         }
                //     }
                // }

                // if (sourcePlacement !== 'PutterBackPaintfill') {
                //     const backPlacement = this.viewController.findPlacement('PutterBackPaintfill');
                //     if (backPlacement) {
                //         const toSelect = backPlacement.components.find((c) => !c.placeholder);
                //         if (toSelect) {
                //             this.viewController.updateComponent(backPlacement.code, toSelect.code);
                //         }
                //     }
                // }
            }
        },

        resetPaintfill() {
            this.clearFrontNestedPanelValidationAttempted('paintfill');

            const placement = this.viewController.findPlacement('PutterFrontPaintfill');

            if (placement) {
                this.clearPaintfill(placement);
            }

            this.clearPaintfillPlacements(PREVIEW_IMAGE_PAINTFILL_COLOR_PLACEMENTS['Face/Sole']);

            this.clearStepSubOptionGroupColorSelections(this.paintfillSubOptionKeysForGroup('Face/Sole'));
        },

        resetTop() {
            this.setTopNoCustomizationSelected(false);

            const placement = this.viewController.findPlacement('PutterTopPaintfill');

            if (placement) {
                this.clearPaintfill(placement);
            }

            this.clearPaintfillPlacements([
                'TopLineColor',
            ]);

            this.clearStepSubOptionGroupColorSelections(['top']);
        },

        resetBack() {
            this.setBackNoCustomizationSelected(false);

            const placement = this.viewController.findPlacement('PutterBackPaintfill');

            if (placement) {
                this.clearPaintfill(placement);
            }

            this.clearPaintfillPlacements([
                'MilledLogoColor',
                'DotHeelColor',
                'DotCenterColor',
                'DotToeColor',
            ]);

            this.clearStepSubOptionGroupColorSelections(['backLogo', 'backCavityDots']);
        },

        resetAllPaintfill() {
            this.resetPaintfill();
            this.resetTop();
            this.resetBack();
            this.keepPaletteColorsForAppliedGroups(['frontInitials', 'frontStamps']);
        },

        clearPaintfillPlacements(placementCodes = []) {
            placementCodes.forEach((placementCode) => {
                const placement = this.viewController.findPlacement(placementCode);

                if (!placement) {
                    return;
                }

                const placeholder = getPlaceholderForPlacement(placement, this.viewController);

                if (placeholder) {
                    this.viewController.updateComponent(placement.code, placeholder.code);
                }
            });
        },

        clearPaintfill(placement) {
            // Reset all selections.
            const group = this.viewController.blueprint.groups.find((x) => x.placements.some((p) => p.code === placement.code));

            if (group) {
                for (const p of group.placements) {
                    // Prefer optional placeholders.
                    let toSelect = p.components.find((x) => x.placeholder && x.placeholderOptional);

                    if (toSelect == null) {
                        toSelect = p.components.find((x) => x.placeholder);
                    }

                    if (toSelect) {
                        const selected = this.viewController.selectedAtPlacement(p.code);
                        if (selected?.code !== toSelect.code) {
                            this.viewController.updateComponent(p.code, toSelect.code);
                        }
                    }
                }
            }
        },

        resetFrontNoCustomization() {
            // Clear the persisted "No Customization" selection for the Face/Sole panel
            this.setFrontNoCustomizationSelected('Face/Sole', false);
        },

        resetStamps() {
            this.clearFrontNestedPanelValidationAttempted('stamps');

            this.viewController.updateComponent('Stamp', 'Stamp-Stamp-Blank');

            this.clearPaintfillPlacements(['StampColor']);

            this.clearStepSubOptionGroupColorSelections(['frontStamps']);

            if (this.isFrontSectionActive('stamps')) {
                this.clearFrontActiveSection('stamps');
            }
        },

        resetShaftBand() {
            this.viewController.updateComponent('PutterShaftBand', 'PutterShaftBand-PutterShaftBand-Blank');
            this.pickDefaultShaftBands(this.viewController);
        },

        async resetAll() {
            this.panelToReset = 'All';
            this.isFinishedLoading = false;

            if (!this.areSpecificationsLocked) {
                await this.resetSpecifications();
            }

            this.resetStamps();

            if (!this.areSpecificationsLocked) {
                this.resetShaft();
            }

            if (!this.areSpecificationsLocked) {
                this.resetGrip();
            }

            await this.resetHeadcover();
            await this.resetCharacterStamping();
            this.resetShaftBand();
            this.resetPaintfill();
            this.resetTop();
            this.resetBack();
            this.resetFrontNoCustomization();
            this.hasChanged = false;

            // Let the existing completion checks evaluate every restored group, including Face/Sole.
            this.groupsSeen = [...new Set([
                ...this.visibleGroups.map((group) => group.name),
                ...this.progressGroups.map((group) => group.name),
            ])];

            this.resetGroupLocks();
            this.stateChanged(false);

            setTimeout(() => { // ensure hasChanged is false when reset is complete
                this.isFinishedLoading = true;
            }, 1000);
        },

        async resetCurrentPanel() {
            const appStore = useAppStore();
            const currentPanelName = this.currentGroup;
            this.panelToReset = currentPanelName;

            switch (currentPanelName) {
            case 'Specifications':
                await this.resetSpecifications();
                break;
            case 'Shaft':
                this.resetShaft();
                break;
            case 'Grip':
                this.resetGrip();
                break;
            case 'Character Stamping':
                await this.resetCharacterStamping();
                break;
            case 'Paintfill':
                this.resetPaintfill();
                break;
            case 'Toe Engraving':
                if (this.resetToeEngraving) {
                    await this.resetToeEngraving();
                }
                break;
            case 'Flight Lines':
                if (this.resetFlightLines) {
                    await this.resetFlightLines();
                }
                break;
            case 'Face/Sole':
                if (appStore.nestedPanels.some((panel) => panel.panelId === 'charactersInitialsOptions')) {
                    appStore.closeNestedPanel();
                    await new Promise((resolve) => { setTimeout(resolve, 200); }); // wait for nested panel to close
                    this.resetCharacterStamping();
                    return;
                }
                if (appStore.nestedPanels.some((panel) => panel.panelId === 'frontStampsOptions')) {
                    appStore.closeNestedPanel();
                    await new Promise((resolve) => { setTimeout(resolve, 200); }); // wait for nested panel to close
                    this.resetStamps();
                    return;
                }
                if (appStore.nestedPanels.some((panel) => panel.panelId === 'frontPaintfillOptions')) {
                    appStore.closeNestedPanel();
                    await new Promise((resolve) => { setTimeout(resolve, 200); }); // wait for nested panel to close
                    this.resetPaintfill();
                    return;
                }
                await this.resetCharacterStamping();
                this.resetStamps();
                this.resetPaintfill();
                this.clearFrontActiveSection();
                this.resetFrontNoCustomization();
                break;
            case 'Top':
                this.resetTop();
                break;
            case 'Back':
                this.resetBack();
                break;
            case 'Shaft Band':
                this.resetShaftBand();
                break;
            default:
                break;
            }

            this.stateChanged(false);

            await new Promise((resolve) => { setTimeout(resolve, 1000); });

            this.panelToReset = null;
        },

        /**
         * Resets customizations.
         */
        async resetCustomization() {
            // Reset to the very first loaded controller.
            const newBeginning = this.startingController.clone();

            // Reset events since the old handles might have closures capturing old data.
            newBeginning.off();

            this.attachControllerEvents(newBeginning);

            this.resetFrontNoCustomization();
            this.showController(newBeginning);

            this.undoStack = [];
            this.groupsSeen = [];
            this.resetGroupLocks();

            this.setErrorState(false);

            const placements = this.visibleGroups?.find((group) => group.name === this.currentGroup)?.placements;

            if (!placements) {
                return;
            }

            await this.resetSpecifications(placements);
            this.resetCharacterStamping(placements);

            this.stateChanged();

            const prefilledGroups = [
                'Specifications',
                'Shaft',
                'Grip',
                'Paintfill',
            ];

            // Mark all groups as seen that are passed into the widget from the app
            prefilledGroups.forEach((group) => {
                this.addGroupToSeen(group);
            });

            // this.setCurrentGroup(this.visibleGroups?.[0].name);
        },

        /**
         * Enable stamping addons.
         */
        useStampingAddons(placementCode, enabled) {
            const selected = this.viewController.selectedAtPlacement(placementCode);

            if (selected.custom?.['v-stamping-supports-snow-symbol']) {
                this.viewController.commitCustomAttributes(placementCode, {
                    'v-stamping-use-symbols-addon': enabled ? 'yes' : 'no',
                });

                // Clear out old selections.
                this.resetStampSymbols();
            }
        },

        /**
         * Sorts options .
         */
        sortOptions(placement, selectedValue) {
            if (placement.components == null) {
                return [];
            }

            const styles = placement.components.filter((option) => {
                if (option.placeholder) {
                    // Placeholders should not be shown to users since they (placeholders) should only ever be present
                    // in the initial state only.
                    //
                    // Unless they are "No Thanks" options.
                    if (!option.placeholderOptional) {
                        return false;
                    }
                }

                // Or if a component is hidden because reasons.
                if (option.custom?.['v-hidden'] === 'yes') {
                    return false;
                }

                return true;
            }).map((option) => {
                let order = null;

                // Put No Thanks buttons at the end.
                if (option.placeholder) {
                    order = 10000;
                } else {
                    order = +(option.custom['v-rank'] || '1');
                }

                // When displaying buttons, prefer to use descriptions, since they would contain more information.
                let label = null;

                if (option.custom['v-preferred-name']) {
                    label = option.custom['v-preferred-name'];
                }

                if (label == null) {
                    label = this.viewController.componentNames(option).join(' ');
                }

                return {
                    label,
                    value: option.code,
                    selected: option.code === selectedValue,
                    order,
                    placeholder: option.placeholder,
                    raw: option,
                };
            });

            styles.sort((a, b) => a.order - b.order);

            return styles;
        },

        buildBaseUrl() {
            // Test mode.
            if (window.location.href.includes('demandware.store')) {
                if (window.CQuotient) {
                    // eslint-disable-next-line max-len
                    return `${window.location.protocol}//${window.location.host}/on/demandware.store/Sites-${window.CQuotient.siteId}-Site/${window.CQuotient.locale}`;
                }
            }

            // Does it look like that the site is using /s/ urls with locales?
            if (/^\/s\/footjoy[a-z-]*\/[a-z][a-z]_[A-Z][A-Z]\//.test(window.location.pathname)) {
                const locale = `${window.location.pathname.split('/').slice(0, 4).join('/')}`;

                return `${window.location.protocol}//${window.location.host}${locale}`;
            }

            // Does it look like that the site is using SFCC /s/ urls without locales?
            if (/^\/s\/footjoy/.test(window.location.pathname)) {
                const locale = `${window.location.pathname.split('/').slice(0, 3).join('/')}`;

                return `${window.location.protocol}//${window.location.host}${locale}`;
            }

            // Does it look like that the site is using locales?
            if (/\/[a-z][a-z]_[A-Z][A-Z]\//.test(window.location.pathname)) {
                const secondSlash = window.location.pathname.indexOf('/', 1);

                const locale = window.location.pathname.substr(0, secondSlash);

                return `${window.location.protocol}//${window.location.host}${locale}`;
            }

            // Fallback.
            return `${window.location.protocol}//${window.location.host}`;
        },

        generateSummaryData() {
            // const options = this.options;

            // console.log('Options', options);
        },

        backTo(url) {
            if (this.options.onCancel) {
                if (this.options.onCancel()) {
                    const appStore = useAppStore();

                    appStore.globalDone();

                    return;
                }
            }

            window.location = url;
        },

        getDancingStyle(code) {
            if (code.endsWith('CPDND')) {
                return 'dancing';
            }

            if (code.endsWith('CPSTD')) {
                return 'straight';
            }

            if (code.endsWith('CPDN')) {
                return 'dancing';
            }

            if (code.endsWith('CPST')) {
                return 'straight';
            }

            return 'straight';
        },

        getDotsStyle(code) {
            if (code.endsWith('CPDND')) {
                return true;
            }

            if (code.endsWith('CPSTD')) {
                return true;
            }

            if (code.endsWith('CPDN')) {
                return false;
            }

            if (code.endsWith('CPST')) {
                return false;
            }

            return false;
        },

        setActiveView(view) {
            this.activeView = view;

            this.updatePreviewImage();
        },

        makeImageUrl(name) {
            if (this.viewController?.blueprint?.imageRootUrl) {
                return `${this.viewController?.blueprint?.imageRootUrl}${name}`;
            }

            return '';
        },

        isSurefitHubLocked(placements) {
            if (this.areSpecificationsLocked) {
                if (placements?.length > 0) {
                    const firstPlacement = placements[0];

                    const code = firstPlacement?.code;

                    if (code === 'PutterParent' || code === 'Shaft') {
                        return true;
                    }
                }
            }

            return false;
        },

        isStampAllowed(component) {
            const code = component.custom['v-id'];

            const logoRestrictions = this.restrictions?.logoRestrictions?.[this.options.locale];

            if (logoRestrictions) {
                if (logoRestrictions.includes(code)) {
                    return false;
                }
            }

            return true;
        },

        buildStepSubOptions() {
            const options = {
                top: [
                    {
                        name: 'TopLineColor',
                        selectedColor: null,
                    },
                ],
                backLogo: [
                    {
                        name: 'MilledLogoColor',
                        selectedColor: null,
                    },
                ],
                backCavityDots: [
                    {
                        name: 'DotHeelColor',
                        selectedColor: null,
                    },
                    {
                        name: 'DotCenterColor',
                        selectedColor: null,
                    },
                    {
                        name: 'DotToeColor',
                        selectedColor: null,
                    },
                ],
                frontPaintfillWeights: [
                    {
                        name: 'ToeWeightColor',
                        selectedColor: null,
                    },
                    {
                        name: 'HeelWeightColor',
                        selectedColor: null,
                    },
                ],
                frontPaintfillLogos: [
                    {
                        name: 'CrownLogoSoleColor',
                        selectedColor: null,
                    },
                    {
                        name: 'ScottyCameronLogoSoleColor',
                        selectedColor: null,
                    },
                    {
                        name: 'FamilyLogoSoleColor',
                        selectedColor: null,
                    },
                    {
                        name: 'ModelLogoSoleColor',
                        selectedColor: null,
                    },
                    {
                        name: 'CrownLogoFaceColor',
                        selectedColor: null,
                    },
                ],
                frontInitials: [],
                frontStamps: [],
                activePaletteSlotByOptionName: {},
            };

            // Delete options without matching placements.
            for (const key of Object.keys(options)) {
                const list = options[key];

                for (let i = 0; i < list.length;) {
                    const real = this.viewController.findPlacement(list[i].name);
                    if (real) {
                        // Read selected color.
                        const option = list[i];

                        if (option.selectedColor == null) {
                            const selected = this.viewController.selectedAtPlacement(option.name);
                            if (selected && !selected.placeholder) {
                                option.selectedColor = selected.custom?.['v-hex'];
                            }
                        }

                        i += 1;
                    } else {
                        list.splice(i, 1);
                    }
                }
            }

            return options;
        },

        ...CustomizerOrderData,
        ...CustomizerCart,
        ...CustomizerRendering,
    },
});
